import React, { useEffect, useState, useRef } from 'react';
import { HtmlFrame } from '../EmailDetail';
import { useTranslation } from 'react-i18next';
import MailBadge from '../MailBadge';
import { useBadgeOnly, BadgeFilterBar } from '../BadgeFilter';
import { ApprovalMailChip } from '../ApprovalBadge';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../utils/api';
import ChainEmail from './ChainEmail';
import { DayFirstDateInput } from '../DocStorage/UploadWizard';
import { useStore } from '../../store';
import { TakenReturnEntry } from '../../types';
import { displayClientName, displayCompanyName } from '../../utils/displayName';
import Pagination, { getPageSize } from '../Pagination';
import Fab from '../Fab';
import { tabColor, rowTint, lighten, darken, menuPill, DOC_GROUP, TAB_GROUPS, EMAIL_CAL_GROUP } from '../../tabPalette';

// An internal office mailbox is never a real client email (historic placeholder). A client leg with
// no valid external email means the customer was NOT notified — surfaced in the list.
const OFFICE_ADDRS = ['office@slf-legal.co', 'office@slf-accounting.co', 'office@samuilawfirm.com'];
const isOfficeAddr = (e: any) => OFFICE_ADDRS.includes(String(e || '').trim().toLowerCase());
const validClientEmail = (e: any) => { const s = String(e || '').trim(); return !!s && !isOfficeAddr(s) && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s); };
const clientNotNotified = (e: any) => e.counterparty === 'client' && !validClientEmail(e.client_email);

// ── Master list of document types (the office's checklist). Alphabetical. ──────
// Owner/Calem 2026-08-31: a third party is either an INDIVIDUAL (a person whose name is typed, and
// who may be emailed) or a government DEPARTMENT picked from this list. A departmental leg is
// internal-only — no client email, no third-party email — so nobody can accidentally mail
// Immigration, or mail the client about a leg that is really an internal hand-over.
export const TR_DEPARTMENTS: string[] = [
  'Immigration', 'Labor Department', 'Land Office', 'Tessaban',
  'Social Security', 'Revenue Department', 'DBD',
  // Not a government office, but it belongs on this list for the same reason the others do: a
  // document handed to a shop is OUT of our hands and still our responsibility, and the leg is
  // internal — nobody should be emailed about it (owner 2026-09-18).
  'Other (Shop etc)',
];

export const DOC_TYPES: string[] = [
  'Balance Sheet', 'Bank Book', 'Bank Letter', 'Bank Statement', 'Bills, Invoices & Receipts',
  'Birth Certificate', 'Building Permit', 'Cash (Payment to SLF)',
  'Cash (Land Office Fees)', "Cashier's Cheque",
  'Cheque (Payment to SLF)', 'Client Accounts (ALL)', 'Company Certificate', 'Company Documents',
  'Company Stamp', 'Court Documents', 'Death Certificate',
  // 'Director Transfer (Signed)' lost its '(Signed)' here, and 'Share Transfer (Signed)' came out beside the
  // plain 'Share Transfer' (Calem, report 478cfa98: "Remove the word "(signed)" from director transfer and
  // share transfer"): each card's Signed tick says it now. Items saved under the old names keep them, and
  // read under the new ones (RENAMED_DOC_LABEL).
  'Director Transfer', 'Divorce Certificate', 'Documents for Translation', 'Educational Certificate',
  // 'EoY BS&A' came out of the menu here (Calem, report c0b2f015: "Remove EoY BS&A from menu -
  // superseded by balance sheet"): the Balance Sheet card's Year + Company does its job. Items saved
  // under it keep that name, as docLabel falls back to the stored type.
  'Food License', 'Hotel License', 'House Book', 'ID Card',
  'Labour Dept (Appointment)',
  'Land Office Agreement', 'Land Office Receipt', 'Last Will & Testament', 'Lease Agreement',
  'Liquor License', 'Loan Agreement', 'Location Map', 'Mail (Letter)', 'Mail (Package)', 'Marriage Certificate',
  'Master Plans', 'Medical Certificate', 'Minutes of the Meeting', 'Non B (M) Documents', 'Non B (S) Documents',
  'Notarized Documents', 'Passport',
  'Passport Photos', 'Payment Receipt', 'Pension Letter', 'Personal Receipt', 'PND 1', 'PND 3',
  'Police Report', 'Power of Attorney (Mortgage)', 'Power of Attorney (Registration)',
  'Power of Attorney (Transfer)', 'Proof of Address', 'Qualifications', 'Rental Accom. License',
  'Rental Accom. License (Temp)', 'Sales & Purchase Agreement', 'Scan (Payment to SLF)', 'Share Transfer',
  // 'Social Security Registration' joined the menu in report 85ddfdbc's 7th opening (Calem: "Add 'social
  // security registration' to the taken and return lists"); until then staff typed it under Other.
  'Social Security Receipt', 'Social Security Registration', 'Swimming Pool License',
  'Title Deed', 'Work Permit', 'Work Permit Photos', 'WP3 Letter', 'Yoga License', 'Yoga License (Temp)',
];

// ── Job-link picker (owner 2026-09-02) ──────────────────────────────────────
// Explicit M:N between a custody leg and jobs. SUGGESTS the same person's jobs (active first,
// matched by normalised name server-side) — but only what the operator ticks is saved.
function JobLinkPicker({ entry, onClose, onSaved }: { entry: any; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [suggested, setSuggested] = useState<any[]>([]);
  const [hits, setHits] = useState<any[]>([]);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [known, setKnown] = useState<Map<string, any>>(new Map());   // id → job (so ticked rows survive a new search)
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    Promise.all([api.trJobLinks(entry.id), api.trJobCandidates(entry.id)]).then(([l, c]: any[]) => {
      const cur = (l.jobs || []).map((j: any) => j.id);
      // Owner 2026-09-02: opening the picker DEFAULT-matches by name — the person's ACTIVE human
      // jobs come pre-ticked (the server sends only those as suggestions). Save is still what
      // decides; nothing is written until then. A leg that already has links keeps its saved set.
      const preset = cur.length ? cur : (c.suggested || []).map((j: any) => j.id);
      setSel(new Set(preset));
      const m = new Map<string, any>();
      for (const j of [...(l.jobs || []), ...(c.suggested || [])]) m.set(j.id, j);
      setKnown(m); setSuggested(c.suggested || []);
    }).catch((e: any) => setErr(e.message));
  }, [entry.id]);
  useEffect(() => {
    const h = setTimeout(() => {
      if (!q.trim()) { setHits([]); return; }
      api.trJobCandidates(entry.id, q.trim()).then((c: any) => {
        setHits(c.hits || []);
        setKnown(prev => { const m = new Map(prev); for (const j of c.hits || []) m.set(j.id, j); return m; });
      }).catch(() => {});
    }, 300);
    return () => clearTimeout(h);
  }, [q, entry.id]);

  const toggle = (id: string) => setSel(s2 => { const n = new Set(s2); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const save = async () => {
    setBusy(true); setErr('');
    try { await api.trSetJobLinks(entry.id, Array.from(sel)); onSaved(); onClose(); }
    catch (e: any) { setErr(e.message); setBusy(false); }
  };
  const row = (j: any) => (
    <label key={j.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderBottom: '1px solid var(--border)', cursor: 'pointer', fontSize: 12.5 }}>
      <input type="checkbox" checked={sel.has(j.id)} onChange={() => toggle(j.id)} style={{ cursor: 'pointer' }} />
      <span style={{ fontWeight: 700, color: '#4F46E5', whiteSpace: 'nowrap' }}>#{Number(j.job_no).toLocaleString()}</span>
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.specific_job || '—'}<span style={{ color: 'var(--text-tertiary)' }}> · {displayCompanyName(j.person)}</span></span>
      {j.closed && <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-tertiary)', border: '1px solid var(--border)', borderRadius: 5, padding: '0 5px' }}>CLOSED</span>}
    </label>
  );
  const ticked = Array.from(sel).map(id => known.get(id)).filter(Boolean);

  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget && !busy) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 10140, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: 560, maxWidth: '100%', maxHeight: '86vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', borderRadius: 14, overflow: 'hidden', boxShadow: 'var(--shadow-md)' }}>
        <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--border)', fontSize: 14.5, fontWeight: 800 }}>
          <i className="ti ti-briefcase" style={{ marginRight: 7, color: 'var(--accent)' }} aria-hidden />
          {t('tr_jl_title')} — {displayCompanyName(`${entry.client_first || ''} ${entry.client_last || ''}`)}
        </div>
        <div style={{ padding: '10px 18px 0' }}>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('tr_jl_search')} autoFocus
            style={{ width: '100%', padding: '8px 11px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 8, background: 'var(--bg-app)', outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 18px 4px' }}>
          {ticked.length > 0 && <>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)', padding: '6px 0 2px' }}>{t('tr_jl_linked')} ({ticked.length})</div>
            {ticked.map(row)}
          </>}
          {suggested.filter(j => !sel.has(j.id)).length > 0 && <>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)', padding: '10px 0 2px' }}>{t('tr_jl_suggested')}</div>
            {suggested.filter(j => !sel.has(j.id)).map(row)}
          </>}
          {hits.filter(j => !sel.has(j.id)).length > 0 && <>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)', padding: '10px 0 2px' }}>{t('tr_jl_results')}</div>
            {hits.filter(j => !sel.has(j.id)).map(row)}
          </>}
          {!suggested.length && !hits.length && !ticked.length && <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', padding: 14 }}>{t('tr_jl_none')}</div>}
          {err && <div style={{ fontSize: 12.5, color: '#B42318', padding: '8px 0' }}>{err}</div>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '12px 18px', borderTop: '1px solid var(--border)' }}>
          <button onClick={onClose} disabled={busy} style={{ padding: '8px 16px', borderRadius: 9, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}>{t('cancel')}</button>
          <button onClick={save} disabled={busy} style={{ padding: '8px 18px', borderRadius: 9, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1 }}>{t('save')}</button>
        </div>
      </div>
    </div>
  );
}

// ── Smart note hints (owner 2026-08-25: "对提示语智能一点,比如 cash 就提示 amount") ─────────────
// Keyword-matched, first hit wins — ORDER MATTERS ("Cashier's Cheque" contains "cash",
// "Company Certificate" contains "certificate"). Returns '' for types with no specific hint,
// caller falls back to the generic i18n placeholder.
function docNoteHint(doc: string): string {
  const d = doc.toLowerCase();
  const has = (...ks: string[]) => ks.some(k => d.includes(k));
  if (has('cheque')) return 'Cheque no., bank, amount…';
  if (has('photos')) return 'How many photos + whose…';
  if (has('cash', '(payment to slf)')) return 'Amount + what it is for (e.g. 18,356 THB — VAT June–July)…';
  if (has('passport')) return 'Passport no. + holder name…';
  if (has('id card')) return 'ID no. + name…';
  if (has('chanote', 'nor sor')) return 'Title deed no. + land plot / location…';
  if (has('bank')) return 'Bank + account no.…';
  if (has('balance sheet', 'bs&a')) return 'Company + year / period…';
  if (has('receipt', 'bills')) return 'Doc no. / period / amount…';
  if (has('pnd')) return 'Tax period + company…';
  if (has('license')) return 'License no. + business name…';
  if (has('building permit')) return 'Permit no. + property…';
  if (has('work permit', 'wp3')) return 'Permit no. + holder name…';
  if (has('house book')) return 'Address / book no.…';
  if (has('power of attorney')) return 'Grantor + purpose…';
  if (has('agreement')) return 'Parties + date…';
  if (has('court', 'police')) return 'Case no. / reference…';
  if (has('mail (')) return 'Sender / tracking no.…';
  if (has('translation')) return 'Which documents + target language…';
  if (has('company', 'minutes', 'stamp')) return 'Company name (+ reg. no.)…';
  if (has('certificate', 'will', 'pension', 'proof of address', 'qualifications')) return 'Whose + issue details…';
  if (has('non b')) return 'Whose + visa reference…';
  if (has('map', 'plans')) return 'Property / project…';
  return '';
}

type Firm = { id: string; name: string; short: string };

// ── Bangkok-time (UTC+7, no DST) helpers ─────────────────────────────────────
const BKK = 'Asia/Bangkok';
// Minute precision only (owner 2026-08-25) — seconds on a custody hand-over are noise.
const fmtTs = (ts: number) => ts
  ? new Date(ts * 1000).toLocaleString('en-GB', { timeZone: BKK, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })
  : '';
// Compact stamp for the inline chips — the year is noise beside three badges on one row.
const shortStamp = (ts: number) => ts
  ? new Date(ts * 1000).toLocaleString('en-GB', { timeZone: 'Asia/Bangkok', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })
  : '';
// Date only, for the chain summary line (the legs keep their own date+time).
const fmtDay = (ts: number) => ts
  ? new Date(ts * 1000).toLocaleDateString('en-GB', { timeZone: BKK, day: '2-digit', month: '2-digit', year: 'numeric' })
  : '—';
const fmtDateTime = (ts: number) => ts
  ? new Date(ts * 1000).toLocaleString('en-GB', { timeZone: BKK, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
  : '';
// unix → "YYYY-MM-DDTHH:mm" Bangkok wall-clock (for <input type=datetime-local>)
function tsToLocalInput(ts: number): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: BKK, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(new Date(ts * 1000));
  const g = (t: string) => parts.find(p => p.type === t)?.value || '00';
  return `${g('year')}-${g('month')}-${g('day')}T${g('hour') === '24' ? '00' : g('hour')}:${g('minute')}`;
}
// "YYYY-MM-DDTHH:mm" Bangkok wall-clock → unix seconds
const localInputToTs = (s: string): number => s ? Math.floor(Date.parse(s + ':00+07:00') / 1000) : Math.floor(Date.now() / 1000);

const firmLabel = (f: Firm) => f.name.includes('Legal') ? 'Legal' : f.name.includes('Accounting') ? 'Accounting' : (f.short || f.name);

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', fontSize: 13, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box',
};
const labelStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 };
const btnGhostSm: React.CSSProperties = { border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', borderRadius: 'var(--radius-md)', padding: '3px 9px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' };
// The note box on each document card — and the two boxes that take its place for the types below.
const noteBoxStyle: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '5px 8px', fontSize: 12, borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-app)', color: 'var(--text-primary)', outline: 'none' };

// ── Structured notes (Calem, report fcc6685f) ────────────────────────────────────────────────
// Thirteen document types get their own boxes in place of the free note, as in Calem's mock-ups:
// Balance Sheet = Year + Company, Bank Book = Account number + Bank, Bills, Invoices & Receipts =
// YYYY-MM + Company. The 4th opening gave Bank Letter and Bank Statement the Bank Book's two boxes
// ("Add the same … user input fields to bank letter and bank statement"). The 5th ("Three more user
// fields to change") gave Building Permit = Number - Year + Issued to, Cash (Payment to SLF) = Price
// + a fixed THB, and Chanote = Type + Number. The 6th headed that last card Title Deed, Chanote being
// one of the types in its dropdown ("Change the heading … from "Chanote" to "Title Deed" and then
// Chanote Nor Sor 3kor and Por Bor Tor is in the dropdown"). The 7th took the catalogue's own
// 'Nor Sor 3 Gor' out of the menu ("Remove Nor Sor 3 Kor from the menu as it's now part of the title
// deed dropdown"); items saved under it keep that name. Report 01a9b1a2 gave Cheque and Scan
// (Payment to SLF) the Cash card's Price + THB ("On the Check and Scan to SLF fields, make them the
// same as the Cash field"). Report efcace02 gave Passport = Purpose + an Option list that follows
// the purpose ("Passport if 90-day report selected (no other) business extension (1 month, 3 months
// or 11 months) …"). Report 0bacef33 gave every License a Number + Company ("All licenses use the
// same fields"): the menu's Food, Hotel, Liquor, Rental Accom., Swimming Pool and Yoga Licenses, and a
// Fitness, Beauty or Massage License added under Other — found by the word License (or Licence) in
// the name, not by a list (noteKindOf). Report 85ddfdbc gave the three payment cards a Purpose after
// the THB ("Add as per screenshot"), and its 2nd opening a month and company after VAT, WHT and
// Accounting. Report fcc6685f's 10th opening gave Social Security Registration a Company box and the
// Complete tick ("Add company dropdown and complete tick box to the field below"): the type wasn't in
// the menu — staff added "Social Security Registration (Signed)" under Other — so it too is found by
// its name. The 11th opening gave Work Permit Photos and Passport Photos a Number box ("Number box.
// As per screenshot same for passport photos"): how many photos, in digits only, with no Complete
// tick and no Company. Report 0bcbe9ec gave ID Card and House Book a Name, a Relationship dropdown
// and a Signed tick ("Change ID card and house book as per screenshot"), saved 'Somchai Jaidee ·
// Wife (Signed)'. Report 85ddfdbc's 7th opening put Social Security Registration in the menu — the
// names typed before it still open in the same card — and a Signed tick in front of its Complete
// ("add a signed tick box in front of the complete tick box"), saved 'Pamela Enterprises (Signed)
// (Complete)'. Report 6a550a3c gave Client Accounts (ALL) the same Company box with no tick ("Make
// this a 'company' dropdown"), saved 'ABC': a kind of its own, 'firm', as 'company' draws Social
// Security Registration's Signed and Complete ticks. Report 6deefcce's 2nd opening gave Cash (Land
// Office Fees) and Cashier's Cheque the payment cards' Price + THB and nothing more ("Change to a
// price field to cashiers' check and cash land office fees"), saved '18,356 THB': a kind of its own,
// 'price', as 'cash' brings the Purpose dropdown and is what paymentOnly counts.
// Report f8a05a69 gave Company Certificate and Company Documents the Company box in place of their
// free note ("Add a 'company' dropdown (to company certificate and company documents) as per
// screenshot"): Company Certificate as Client Accounts (ALL) has it, 'firm', saved 'ABC'. Company
// Documents also takes the date the documents were issued ("With company documents also add an issue
// date field after the company dropdown"), typed or picked day-first, saved 'ABC · 28/09/2026': a kind
// of its own, 'firmdate'.
// Report 478cfa98 gave Director Transfer and Share Transfer a Name, a Company box and a Signed tick in
// place of their free note ("change (both director and share) fields to 'name' (Text box) 'company'
// dropdown and 'signed' radio button"), saved 'Somchai Jaidee · ABC (Signed)': a kind of its own,
// 'transfer', as 'person' has the Relationship dropdown. The tick is a checkbox, as ID Card's is — a
// lone radio button could not be unticked.
// The values are written into the item's ordinary note ('2025 · ABC Co.,Ltd',
// '304-2561 · Somchai Jaidee', '3,256 THB · VAT · 2026-09 · ABC', 'Nor Sor 3 Kor · 1234', 'Non IMM
// (Business) Extension - 3 Months', '112/12 · Fit Koh', 'Pamela Enterprises', '6') and read back
// from it, so the stored record, the chain names and the client's receipt carry it exactly as they
// carry a typed note. Every other type keeps the free note, bar Work Permit (WorkPermitPurposeRow)
// and Medical Certificate (MedicalCertRow).
// ⛔ The separator is part of the stored text — change it and the notes already written stop
// splitting back into their boxes. The same holds for the permit's '-', the cash note's THB and the
// passport's ' - '.
type NoteKind = 'year' | 'bank' | 'month' | 'permit' | 'cash' | 'deed' | 'passport' | 'license' | 'company' | 'firm' | 'firmdate' | 'number' | 'person' | 'price' | 'transfer';
const NOTE_KIND: Record<string, NoteKind> = {
  'Balance Sheet': 'year', 'Bank Book': 'bank', 'Bank Letter': 'bank', 'Bank Statement': 'bank', 'Bills, Invoices & Receipts': 'month',
  'Building Permit': 'permit', 'Cash (Payment to SLF)': 'cash', 'Title Deed': 'deed',
  'Cheque (Payment to SLF)': 'cash', 'Scan (Payment to SLF)': 'cash', 'Passport': 'passport',
  'Work Permit Photos': 'number', 'Passport Photos': 'number',
  'ID Card': 'person', 'House Book': 'person', 'Client Accounts (ALL)': 'firm',
  'Cash (Land Office Fees)': 'price', "Cashier's Cheque": 'price',
  'Company Certificate': 'firm', 'Company Documents': 'firmdate',
  'Director Transfer': 'transfer', 'Share Transfer': 'transfer',
  // The Title Deed card's name before the 6th opening. Items saved under it keep that name (docLabel
  // shows it as Title Deed) and still open in the Type + Number boxes.
  'Chanote': 'deed',
  // The two Transfers' names before report 478cfa98. Items saved under them keep those names (docLabel
  // shows them without the '(Signed)') and open in the same boxes.
  'Director Transfer (Signed)': 'transfer', 'Share Transfer (Signed)': 'transfer',
};
const noteKindOf = (type: string): NoteKind | undefined =>
  NOTE_KIND[type] || (/\bLicen[cs]e\b/i.test(type) ? 'license' : /^Social Security Registration\b/i.test(type) ? 'company' : undefined);
const NOTE_SEP = ' · ';
const TR_BANKS: string[] = ['Kasikorn', 'Bangkok Bank', 'SCB', 'Krungthai', 'Krungsri', 'ttb', 'UOB', 'GSB', 'CIMB', 'Other'];
// A bank typed by hand before the dropdown existed ("Kasikorn Bank", "KBank") still finds its entry.
// Only a note that is nothing BUT a bank name: "Kasikorn 123-4-56789-0" is an account number too,
// and reading it as the bank would hide the number.
const BANK_ALIAS: Record<string, string> = { kbank: 'Kasikorn', kasikornbank: 'Kasikorn', bbl: 'Bangkok Bank', siamcommercial: 'SCB', ktb: 'Krungthai', bay: 'Krungsri', tmb: 'ttb', tmbthanachart: 'ttb', governmentsavings: 'GSB', cimbthai: 'CIMB' };
const bankKey = (s: string) => s.toLowerCase().replace(/\bbank\b/g, '').replace(/[^a-z]/g, '');
const bankOf = (s: string): string => {
  const k = /\d/.test(s) ? '' : bankKey(s);
  return k ? (TR_BANKS.find(b => bankKey(b) === k) || BANK_ALIAS[k] || '') : '';
};
// Title Deed's Type dropdown. A note that is nothing but a deed type, in any case or as the system spells
// it elsewhere (the catalogue's former "Nor Sor 3 Gor", Due Diligence's "Por Tor Bor 5"), finds its entry.
const TR_DEEDS: string[] = ['Chanote', 'Nor Sor 3 Kor', 'Por Bor Tor'];
const DEED_ALIAS: Record<string, string> = { norsor3gor: 'Nor Sor 3 Kor', norsorsamgor: 'Nor Sor 3 Kor', porbortor5: 'Por Bor Tor', portorbor5: 'Por Bor Tor' };
const deedKey = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
const deedOf = (s: string): string => {
  const k = deedKey(s);
  return k ? (TR_DEEDS.find(d => deedKey(d) === k) || DEED_ALIAS[k] || '') : '';
};
// A Building Permit's Number and Year go into the note as one, '304-2561', the way the permits number
// themselves. No spaces round the dash: " - " is where a note is cut back to its subject (purposeOf),
// which would part the number from its year.
const permitNoYear = (s: string): [string, string] => {
  const m = s.match(/^(.*)-(\d{4})$/);
  return m ? [m[1], m[2]] : [s, ''];
};
const permitHead = (no: string, year: string): string => (year.trim() ? `${no.trim()}-${year.trim()}` : no);
// Cash, Cheque and Scan (Payment to SLF): the Price box with THB fixed after it, saved '18,356 THB' —
// still a note that is only an amount, which the chain names and the job auto-link (MONEY_ONLY here
// and in services/trJobAutoLink.ts) pass over. A note typed before the box — a cheque's number and
// bank, say — shows in it word for word, less a trailing THB, and stays as it was until the box is
// edited. Cash (Land Office Fees) and Cashier's Cheque ('price') have this box too, and never the
// Purpose below (report 6deefcce, 2nd opening).
// On a taken record that holds nothing but these payments a Purpose dropdown follows the THB (Calem,
// report 85ddfdbc), saved after the separator: '3,256 THB · VAT'. That note is no longer only an
// amount, so the job auto-link reads it as it reads any typed note — a Purpose that is one of the
// client's jobs (an extension, a work permit) can propose that job, which someone still confirms.
// Where the dropdown doesn't show — a return, or a record that also holds a document — a note opens
// whole in the Price box, as before.
// VAT, WHT and Accounting also take the month and the company the payment is for (Calem, report
// 85ddfdbc, 2nd opening: "Add a YYYYY-MM (select) and company (drop down) if the user chooses VAT,
// WHT, or Accounting"): the Bills card's YYYY-MM and Contacts Company boxes, on a line under the
// Price, saved after the Purpose: '3,256 THB · VAT · 2026-09 · ABC'.
// Property Tax follows Accounting (Calem, report 6deefcce: "Add property tax to this drop-down list").
// It takes no Month or Company line, so it is not in CASH_MONTHLY: saved '5,000 THB · Property Tax'.
const TR_CASH_PURPOSES: string[] = [
  'VAT', 'WHT', 'Accounting', 'Property Tax', 'Non IMM (Business) Extension', 'Non IMM (Dependent) Extension',
  'Non IMM (Retirement) Extension', 'Work Permit New', 'Work Permit Renew',
];
const CASH_MONTHLY: string[] = ['VAT', 'WHT', 'Accounting'];
// A note's Price, Purpose, Month and Company. The Purpose is the first part that is a listed purpose:
// what comes before it is the Price, and what follows it the Month and Company, read as a Bills note
// is (splitNote) — so '3,256 THB · VAT', saved before those boxes, opens with them empty. A note with
// no listed purpose reads as it did: the Purpose is what follows the last separator — one the list
// doesn't hold shows as an extra entry in the dropdown — or there is none.
const cashParts = (note: string, purposeShown: boolean): [string, string, string, string] => {
  const n = String(note || '');
  const price = (s: string) => s.replace(/\s*THB\s*$/i, '');
  if (!purposeShown) return [price(n), '', '', ''];
  const parts = n.split(NOTE_SEP);
  const k = parts.findIndex(p => TR_CASH_PURPOSES.includes(p.trim()));
  if (k >= 0) return [price(parts.slice(0, k).join(NOTE_SEP)), parts[k].trim(), ...splitNote('month', parts.slice(k + 1).join(NOTE_SEP))];
  const i = n.lastIndexOf(NOTE_SEP);
  return i < 0 ? [price(n), '', '', ''] : [price(n.slice(0, i)), n.slice(i + NOTE_SEP.length).trim(), '', ''];
};
const cashNote = (price: string, purpose: string, month = '', company = ''): string =>
  [price.trim() ? `${price.trim()} THB` : '', purpose, month, company].map(s => s.trim()).filter(Boolean).join(NOTE_SEP);
// The Price box puts commas into a plain number as it is typed, the en-US way (3256 → 3,256), and
// when it is left pads decimals that were typed to two places (3,256.5 → 3,256.50). Nothing is
// rounded, and any other text — a cheque's number — stays as typed.
const PRICE_NUM = /^\d[\d,]*(\.\d*)?$/;
const groupPrice = (s: string): string => {
  if (!PRICE_NUM.test(s)) return s;
  const [whole, dec] = s.split('.');
  return whole.replace(/,/g, '').replace(/^0+(?=\d)/, '').replace(/\B(?=(\d{3})+$)/g, ',') + (dec === undefined ? '' : `.${dec}`);
};
const padPrice = (s: string): string => (PRICE_NUM.test(s) ? s.replace(/\.(\d?)$/, (_, d: string) => `.${d.padEnd(2, '0')}`) : s);
// Passport: the Purpose, and the Options that purpose has, as on Calem's screenshot (report efcace02);
// 90 Day Report has none. Saved 'Non IMM (Business) Extension - 3 Months', or just '90 Day Report' —
// the job titles' own form, with ' - ' rather than NOTE_SEP, so the note is cut back to its purpose
// (purposeOf here, subjectOf in services/trJobAutoLink.ts) and still finds the client's job.
const TR_PASSPORT: [string, string[]][] = [
  ['90 Day Report', []],
  ['Non IMM (Business) Extension', ['1 Month', '3 Months', '11 Months']],
  ['Non IMM (Dependent) Extension', ['1 Month', '3 Months', '11 Months', '12 Months']],
  ['Non IMM (Retirement) Extension', ['3 Months', '12 Months']],
  ['Stamp Transfer', ['New', 'Old']],
  ['Work Permit', ['New', 'Renew', 'Update', 'Cancel']],
  ['Re-entry Stamp', ['Single', 'Multi']],
];
const passportOptions = (purpose: string): string[] => TR_PASSPORT.find(([p]) => p === purpose)?.[1] || [];
// A purpose typed in any case, or as the system writes it elsewhere: the archive's "Non B" and the
// reminders' "Re-entry Permit". "Non-IMM" and "Non Immigrant" need no entry of their own — the key
// drops the hyphen and reads Immigrant as IMM.
const PASSPORT_ALIAS: [string, string][] = [
  ['Non B Extension', 'Non IMM (Business) Extension'], ['Non IMM B Extension', 'Non IMM (Business) Extension'],
  ['Re-entry Permit', 'Re-entry Stamp'],
];
const passportKey = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '').replace(/^nonimmigrant/, 'nonimm');
const passportPurposeOf = (s: string): string => {
  const k = passportKey(s);
  return k ? (TR_PASSPORT.find(([p]) => passportKey(p) === k)?.[0] || PASSPORT_ALIAS.find(([x]) => passportKey(x) === k)?.[1] || '') : '';
};
// A Passport note's Purpose and Option: split at the first ' - ' (or ' – ', the dash the completion
// documents write). A note that doesn't open with a purpose — the passport number and holder name
// typed before these boxes — stays whole as the Purpose, word for word, and an option the purpose
// doesn't list stays as written; each shows as an extra entry in its dropdown, so an item saved
// without touching them keeps exactly what was recorded.
const passportParts = (note: string): [string, string] => {
  const n = String(note || '').trim();
  const m = n.match(/ [-–] /);
  const p = passportPurposeOf(m ? n.slice(0, m.index) : n);
  if (!p) return [n, ''];
  const rest = m ? n.slice((m.index || 0) + m[0].length).trim() : '';
  return [p, passportOptions(p).find(o => passportKey(o) === passportKey(rest)) || rest];
};
const passportNote = (purpose: string, option: string): string => [purpose.trim(), option.trim()].filter(Boolean).join(' - ');
// A Passport's re-entry stamp (Calem, report 4122b0f4: "Add another (optional) dropdown for passport and
// instead of including this information on the same line, put the '+ no/single/multi re-entry stamp'
// into the comments box"): a dropdown under the Purpose and Option that writes its pick into the
// record's Comments, on a line of its own, worded as on Calem's screenshot — in English whatever the
// screen's language, as Comments go out on the client's notice. The passport's note never holds it, so
// the job auto-link and the chain names read the note as before. The dropdown reads its value back from
// Comments — a line that is one of the three, give or take case, spaces, the + and the hyphen; a line
// with any other word on it is the writer's own and is left alone — so an edited record opens on what
// was saved. A pick takes the place of the re-entry line already there and drops any other; the blank
// entry takes them out. The Re-entry Stamp purpose above is a separate thing and stays.
const TR_REENTRY: [string, string][] = [
  ['+ NO Re-entry Stamp', 'tr_fld_reentry_no'],
  ['+ Single Re-entry Stamp', 'tr_fld_reentry_single'],
  ['+ Multi Re-entry Stamp', 'tr_fld_reentry_multi'],
];
const reentryKey = (s: string) => s.toLowerCase().replace(/[\s+-]/g, '');
const reentryOf = (line: string): string => TR_REENTRY.find(([r]) => reentryKey(r) === reentryKey(line))?.[0] || '';
const reentryIn = (comments: string): string => comments.split('\n').map(reentryOf).find(Boolean) || '';
const withReentry = (comments: string, pick: string): string => {
  const lines = comments.split('\n');
  const at = lines.findIndex(l => !!reentryOf(l));
  if (at < 0) return pick ? [comments.replace(/\s+$/, ''), pick].filter(Boolean).join('\n') : comments;
  return lines.flatMap((l, i) => (i === at ? (pick ? [pick] : []) : reentryOf(l) ? [] : [l])).join('\n');
};
// ID Card and House Book (Calem, report 0bcbe9ec): whose card or book it is, their Relationship, and the
// Signed tick, saved 'Somchai Jaidee · Wife' — ' (Signed)' after it when ticked (withSigned). The
// Relationship is what follows the last separator, and only when it is one of the list, in any case; a
// note that is nothing but a relationship opens in the dropdown. Anything else — an ID number and name
// typed before these boxes — opens in the Name box word for word, so an item saved without touching
// them keeps exactly what was recorded.
const TR_RELATIONS: string[] = ['Wife', 'Husband', 'Shareholder', 'Director', 'Lessor'];
const relationOf = (s: string): string => TR_RELATIONS.find(r => r.toLowerCase() === s.trim().toLowerCase()) || '';
const personParts = (note: string): [string, string] => {
  const n = String(note || '');
  const i = n.lastIndexOf(NOTE_SEP);
  const r = relationOf(i < 0 ? n : n.slice(i + NOTE_SEP.length));
  if (!r) return [n, ''];
  return [i < 0 ? '' : n.slice(0, i), r];
};
// Company Documents (report f8a05a69): the company, then the Issue Date, saved 'ABC · 28/09/2026'. The
// date comes off only when what follows the last separator is shaped as the Issue Date box takes a date
// (DayFirstDateInput: 28/09/2026, or 28.9.2026 and the like as typed); a note that is nothing but a date
// opens in the Issue Date box. Anything else — a company name and registration number typed before these
// boxes — opens whole in the Company box, so an item saved without touching them keeps what was recorded.
const DMY_SHAPE = /^\d{1,2}[/.-]\d{1,2}[/.-]\d{4}$/;
const firmDateParts = (note: string): [string, string] => {
  const n = String(note || '');
  const i = n.lastIndexOf(NOTE_SEP);
  const d = (i < 0 ? n : n.slice(i + NOTE_SEP.length)).trim();
  if (!DMY_SHAPE.test(d)) return [n, ''];
  return [i < 0 ? '' : n.slice(0, i), d];
};
// "2025-9", "2025/09", "09/2025", "9.2025" → "2025-09"; '' when it is not a month.
const tidyYm = (s: string): string => {
  const v = String(s || '').trim();
  const m = v.match(/^(\d{4})[/.-](\d{1,2})$/) || v.match(/^(\d{1,2})[/.-](\d{4})$/);
  if (!m) return '';
  const [y, mo] = m[1].length === 4 ? [m[1], m[2]] : [m[2], m[1]];
  return Number(mo) >= 1 && Number(mo) <= 12 ? `${y}-${mo.padStart(2, '0')}` : '';
};
// A note's two halves. With the separator it splits there (a Bank Book's bank is the LAST part, the
// first box of the other kinds the FIRST). Without one it is a single value: it goes to the dropdown
// half when it is a year, a month, a bank or a deed type, to a permit's Number - Year when it is one
// word with a digit in it ('304-2561'), to a License's Number when it has a digit anywhere in it
// ('112/12', or 'No. 112/12 (Fit Koh)' typed before the boxes; Thai digits count), and otherwise — a
// note typed before these boxes, or a License's company picked without a number — to the other half,
// word for word (for a permit that is Issued to, the box wide enough to read it). A Director or Share
// Transfer's goes whole to its Name, as nothing tells a name from a company (report 478cfa98) — a
// Company picked with no Name opens there too. Joining the halves gives back the note they came from,
// so an item that is opened and saved without touching them keeps exactly what was recorded.
const splitNote = (kind: NoteKind, note: string): [string, string] => {
  const n = String(note || '');
  const i = kind === 'bank' ? n.lastIndexOf(NOTE_SEP) : n.indexOf(NOTE_SEP);
  if (i >= 0) return [n.slice(0, i), n.slice(i + NOTE_SEP.length)];
  const v = n.trim();
  if (kind === 'transfer') return [v, ''];
  if (kind === 'bank') return bankOf(v) ? ['', v] : [v, ''];
  if (kind === 'deed') return deedOf(v) ? [v, ''] : ['', v];
  if (kind === 'permit') return /^\S*\d\S*$/.test(v) ? [v, ''] : ['', v];
  if (kind === 'license') return /[0-9๐-๙]/.test(v) ? [v, ''] : ['', v];
  return (kind === 'year' ? /^\d{4}$/.test(v) : !!tidyYm(v)) ? [v, ''] : ['', v];
};
const joinNote = (a: string, b: string): string => [a.trim(), b.trim()].filter(Boolean).join(NOTE_SEP);
// A note as the screen shows it: the Company half of a Balance Sheet, Bills or License note without
// its legal form, so '2025 · A4 Co., Ltd.' reads '2025 · A4' (Calem, report fcc6685f, 3rd opening:
// "Remove any remaining co-limiteds or partnership limiteds from company names in the system").
// Display only — the record keeps the note as written, and the form still splits and saves the
// stored text.
const shownNote = (type: string, note: string): string => {
  const kind = noteKindOf(type);
  if (kind !== 'year' && kind !== 'month' && kind !== 'license') return note;
  const [a, b] = splitNote(kind, note);
  const c = displayCompanyName(b);
  return c && c !== b.replace(/\s+/g, ' ').trim() ? joinNote(a, c) : note;
};
// Balance Sheet years: this year and the ten before it, by the Bangkok calendar.
const bsYears = (): string[] => {
  const y = Number(new Intl.DateTimeFormat('en-GB', { timeZone: BKK, year: 'numeric' }).format(new Date()));
  return Array.from({ length: 11 }, (_, k) => String(y - k));
};
// A saved value the list doesn't hold (a year further back, a bank typed by hand) stays selectable.
const withValue = (list: string[], v: string): string[] => (v && !list.includes(v) ? [...list, v] : list);

// "YYYY-MM" as text, the way the mock-up writes it. A native month box is drawn in the browser's own
// words and order ("September 2026") — the trap DayFirstDateInput (DocStorage) avoids for dates, so
// this is the same build: a text box, and a calendar button that opens a hidden native month picker
// where the browser has one. A typed "2025/9" is tidied to 2025-09 when the box loses focus.
function YearMonthInput({ value, onChange, box }: { value: string; onChange: (ym: string) => void; box: React.CSSProperties }) {
  const { t } = useTranslation();
  const pickerRef = useRef<HTMLInputElement>(null);
  const ym = tidyYm(value);
  return (
    <div style={{ position: 'relative' }}>
      <input value={value} onChange={e => onChange(e.target.value)} onBlur={() => { if (ym && ym !== value) onChange(ym); }}
        placeholder={t('tr_fld_month_ph')} maxLength={10} autoComplete="off" style={{ ...box, paddingRight: 24 }} />
      {/* Invisible but laid out over the box, so the browser hangs its calendar just below it. */}
      <input ref={pickerRef} type="month" value={ym} onChange={e => onChange(e.target.value)} tabIndex={-1} aria-hidden
        style={{ ...box, position: 'absolute', left: 0, top: 0, opacity: 0, pointerEvents: 'none' }} />
      <button type="button" onClick={() => { try { pickerRef.current?.showPicker(); } catch { /* no month picker here — typing still works */ } }}
        title={t('tr_fld_month_pick')} aria-label={t('tr_fld_month_pick')}
        style={{ position: 'absolute', right: 5, top: '50%', transform: 'translateY(-50%)', padding: 1, border: 'none', background: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
        <i className="ti ti-calendar" style={{ fontSize: 13 }} aria-hidden />
      </button>
    </div>
  );
}

// The Company half: every company in Contacts, with a search box (Calem's answer (a)). The list opens
// as a fixed overlay under the box, as the Quotation form's ClientPicker does — the document list it
// sits in scrolls, and would clip a list hung inside it.
// Each company shows without its legal form, "A4", not "A4 Co., Ltd." as Contacts files it (Calem,
// report fcc6685f, 3rd opening), and that shorter name is what a pick writes into the note. Two
// filings that come out the same ("A4 Co., Ltd." and "A4 Company Limited") are one entry; the search
// still finds a company by its name as filed. A note saved before this keeps the name it was written
// with until another company is picked — the box shows it tidied all the same.
// A company Contacts doesn't hold is written in (Calem, report 01a9b1a2, 2nd opening: "Allow 'other'
// user input for this dropdown 'Company'"): while the search box holds text that isn't a company's
// name, as shown or as filed, an Other row pinned under the list saves that text as typed — Enter
// does the same when nothing matches. It goes into the note as a pick does, so the note keeps its
// Year · Company form, and the box shows it as it shows any saved name. The search box takes 150
// characters, as Issued to does.
const companyChoices = (companies: string[]): { name: string; filed: string[] }[] => {
  const by = new Map<string, { name: string; filed: string[] }>();
  for (const c of companies) {
    const name = displayCompanyName(c);
    const cur = by.get(name.toLowerCase());
    if (cur) cur.filed.push(c.toLowerCase()); else by.set(name.toLowerCase(), { name, filed: [c.toLowerCase()] });
  }
  return Array.from(by.values()).sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));
};
function ContactCompanyPicker({ value, companies, onPick, box }: { value: string; companies: string[] | null; onPick: (name: string) => void; box: React.CSSProperties }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const choices = React.useMemo(() => companyChoices(companies || []), [companies]);
  const shown = value ? displayCompanyName(value) : '';
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<React.CSSProperties | null>(null);
  useEffect(() => {
    if (!open) return;
    // Under the box — or above it when the box sits too low on the screen for the list to fit.
    const recalc = () => {
      const el = triggerRef.current; if (!el) return;
      const r = el.getBoundingClientRect();
      const below = window.innerHeight - r.bottom;
      const up = below < 260 && r.top > below;
      setPos({ left: r.left, width: r.width, ...(up ? { bottom: window.innerHeight - r.top + 2 } : { top: r.bottom + 2 }), maxHeight: Math.max(160, Math.min(300, (up ? r.top : below) - 12)) });
    };
    recalc();
    window.addEventListener('scroll', recalc, true); window.addEventListener('resize', recalc);
    return () => { window.removeEventListener('scroll', recalc, true); window.removeEventListener('resize', recalc); };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const tgt = e.target as Node;
      if (triggerRef.current?.contains(tgt) || listRef.current?.contains(tgt)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  const ql = q.trim().toLowerCase();
  const matches = choices.filter(c => !ql || c.name.toLowerCase().includes(ql) || c.filed.some(f => f.includes(ql)));
  // The Other row's text — none while Contacts is still loading.
  const other = ql && companies !== null && !choices.some(c => c.name.toLowerCase() === ql || c.filed.includes(ql)) ? q.trim() : '';
  const isOn = (c: { name: string }) => !!shown && c.name.toLowerCase() === shown.toLowerCase();
  const pick = (name: string) => { onPick(name); setOpen(false); };
  const optStyle = (on: boolean): React.CSSProperties => ({ padding: '7px 10px', fontSize: 12.5, cursor: 'pointer', fontWeight: on ? 700 : 500, background: on ? 'var(--bg-active)' : 'transparent', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' });
  const hover = (on: boolean) => ({
    onMouseEnter: (e: React.MouseEvent<HTMLDivElement>) => { if (!on) e.currentTarget.style.background = 'var(--bg-active)'; },
    onMouseLeave: (e: React.MouseEvent<HTMLDivElement>) => { if (!on) e.currentTarget.style.background = 'transparent'; },
  });
  return (
    <div style={{ position: 'relative' }}>
      <button type="button" ref={triggerRef} onClick={() => { setQ(''); setOpen(o => !o); }} title={shown || undefined}
        style={{ ...box, textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: value ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>{shown || t('tr_fld_company')}</span>
        <i className="ti ti-chevron-down" style={{ fontSize: 13, color: 'var(--text-tertiary)', flexShrink: 0 }} aria-hidden />
      </button>
      {open && pos && (
        <div ref={listRef} style={{ position: 'fixed', ...pos, zIndex: 1100, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow-md)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: 6, borderBottom: '1px solid var(--border)' }}>
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={t('tr_fld_company_search')} maxLength={150} autoComplete="off"
              onKeyDown={e => { if (e.key === 'Escape') setOpen(false); else if (e.key === 'Enter' && (matches.length || other)) { e.preventDefault(); pick(matches.length ? matches[0].name : other); } }}
              style={box} />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {value && <div onClick={() => pick('')} style={{ ...optStyle(false), color: 'var(--text-tertiary)' }} {...hover(false)}>—</div>}
            {companies === null
              ? <div style={{ padding: 10, fontSize: 12, color: 'var(--text-tertiary)' }}>…</div>
              : matches.length
                ? matches.map(c => <div key={c.name} onClick={() => pick(c.name)} style={optStyle(isOn(c))} {...hover(isOn(c))}>{c.name}</div>)
                : <div style={{ padding: 10, fontSize: 12, color: 'var(--text-tertiary)' }}>{t('tr_fld_company_none')}</div>}
          </div>
          {other && (
            <div onClick={() => pick(other)} style={{ ...optStyle(false), flexShrink: 0, borderTop: '1px solid var(--border)', borderRadius: '0 0 7px 7px' }} {...hover(false)}>
              {t('tr_fld_company_other', { name: other })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// The Complete tick (Calem, report 53a19ac8, 2nd opening: "add a tick box after number and company for
// the word (Complete) for every license, every permit, and every extension") — staff were typing
// "(Complete)" into a License's Company box. It follows the boxes of every License and the Building
// Permit, the Option of a Passport whose Purpose is any of TR_PASSPORT's, and the Work Permit
// card's Purpose. A tick is saved as a '(Complete)' tail on the note, the form staff already typed:
// the chain names (NOTE_STATUS) and the job auto-link (services/trJobAutoLink.ts) take it off before
// they read a note, so 'Non IMM (Business) Extension - 3 Months (Complete)' still finds its job. A note
// ending in a status COMPLETE_TAIL knows opens ticked, its boxes read without it — 'Bamboo (Complete)' is
// Company Bamboo, ticked. Unticking takes the tail off; a card left alone keeps its note as stored.
// The Passport had it for the three extensions and Work Permit alone until report fcc6685f, 9th opening
// ("Add a complete tick box for all of these drop-down options"): 90 Day Report, Stamp Transfer and
// Re-entry Stamp have it too, 90 Day Report's after its greyed-out Option. A passport note typed before
// the Purpose dropdown is none of TR_PASSPORT's purposes, so it has no tick. Social Security
// Registration's Company box has it too (10th opening), saved 'Pamela Enterprises (Complete)'.
// ID Card and House Book have the same box labelled Signed (report 0bcbe9ec), saved as a '(Signed)'
// tail that the chain names and the job auto-link take off too. Each tick reads only its own tail: a
// License note ending '(Signed)' opens whole, as before, and an ID Card's '(Complete)' stays in its Name.
// Director Transfer and Share Transfer have that Signed box as well (report 478cfa98).
// Social Security Registration has both, Signed in front of Complete (report 85ddfdbc, 7th opening),
// saved in that order: 'Pamela Enterprises (Signed) (Complete)'. Its note loses the Complete tail first
// and then the Signed one, so each tick opens as it was saved and the Company box reads the bare name;
// NOTE_STATUS, here and in the job auto-link, takes every status tail off the end.
const COMPLETE_TAIL = /\s*\((?:complete|completed|done)\.?\)\s*$/i;
const SIGNED_TAIL = /\s*\(signed\.?\)\s*$/i;
const hasCompleteTick = (kind: NoteKind, first: string): boolean =>
  kind === 'license' || kind === 'permit' || kind === 'company' || (kind === 'passport' && TR_PASSPORT.some(([p]) => p === first));
const withComplete = (note: string, on: boolean): string => (on ? `${note.trim()} (Complete)`.trim() : note);
const withSigned = (note: string, on: boolean): string => (on ? `${note.trim()} (Signed)`.trim() : note);
function CompleteTick({ on, onToggle, label }: { on: boolean; onToggle: (on: boolean) => void; label?: string }) {
  const { t } = useTranslation();
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', alignSelf: 'center', gap: 5, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0, cursor: 'pointer', color: on ? '#166534' : 'var(--text-secondary)' }}>
      <input type="checkbox" checked={on} onChange={e => onToggle(e.target.checked)} />
      {label || t('tr_fld_complete')}
    </label>
  );
}

// The boxes of one structured note. Their values are held here while the operator types — the note
// gets them trimmed, and a space typed at the end of a box must not vanish mid-word.
// A payment card shows its Purpose only on a taken record that holds nothing but payments (report
// 85ddfdbc) — recType and paymentOnly are the form's.
// comments and setComments are the form's Comments box, which the Passport card's Re-entry dropdown
// reads and writes (report 4122b0f4).
function NoteFieldsRow({ kind, note, companies, recType, paymentOnly, comments, setComments, onChange }: { kind: NoteKind; note: string; companies: string[] | null; recType: 'taken' | 'returned'; paymentOnly: boolean; comments: string; setComments: (f: (c: string) => string) => void; onChange: (note: string) => void }) {
  const { t } = useTranslation();
  const purposeShown = kind === 'cash' && recType === 'taken' && paymentOnly;
  const read = (n: string): string[] => {
    if (kind === 'cash' || kind === 'price') return cashParts(n, purposeShown);
    if (kind === 'passport') return passportParts(n);
    if (kind === 'person') return personParts(n);
    if (kind === 'firmdate') return firmDateParts(n);
    // One box: the whole note, separators and all, is the company — a note typed before it shows there.
    // So it is for the photos' Number: a count saved before the box, '6', opens in it as it is.
    if (kind === 'company' || kind === 'firm' || kind === 'number') return [n.trim()];
    const [x, y] = splitNote(kind, n);
    if (kind === 'bank') return [x, bankOf(y) || y.trim()];
    if (kind === 'deed') return [deedOf(x) || x.trim(), y];
    return kind === 'permit' ? [...permitNoYear(x), y] : [x, y];
  };
  // Ticked when the note ends in the card's own tail — Signed on ID Card, House Book, Director Transfer
  // and Share Transfer, Complete on the rest — and the card has the tick; the boxes then read the note
  // without it. Any other note opens whole, as before.
  // Social Security Registration's card also has a Signed tick, in front of its Complete (report
  // 85ddfdbc, 7th opening): once the Complete tail is off, a Signed one comes off too, so 'Pamela
  // Enterprises (Signed) (Complete)' opens with both ticked and Pamela Enterprises in the box.
  const signedOnly = kind === 'person' || kind === 'transfer';
  const tail = signedOnly ? SIGNED_TAIL : COMPLETE_TAIL;
  const withTail = signedOnly ? withSigned : withComplete;
  const hasTick = (first: string) => signedOnly || hasCompleteTick(kind, first);
  const [done, setDone] = useState(() => tail.test(note) && hasTick(read(note.replace(tail, ''))[0]));
  const [signed, setSigned] = useState(() => kind === 'company' && SIGNED_TAIL.test(done ? note.replace(tail, '') : note));
  const [vals, setVals] = useState<string[]>(() => {
    const n = done ? note.replace(tail, '') : note;
    return read(signed ? n.replace(SIGNED_TAIL, '') : n);
  });
  const [a = '', b = '', c = '', d = ''] = vals;
  // Set by the first keystroke in the Price box: a price saved before the commas keeps its own form
  // until somebody types in it.
  const priceTyped = useRef(false);
  // The company card's Signed tail goes on before its Complete one.
  const put = (nv: string[], on: boolean, sig = signed) => {
    setVals(nv); setDone(on); setSigned(sig);
    onChange(withTail(kind === 'cash' || kind === 'price' ? cashNote(nv[0], nv[1], nv[2], nv[3]) : kind === 'permit' ? joinNote(permitHead(nv[0], nv[1]), nv[2])
      : kind === 'passport' ? passportNote(nv[0], nv[1]) : kind === 'company' ? withSigned(nv[0].trim(), sig) : kind === 'firm' || kind === 'number' ? nv[0].trim() : joinNote(nv[0], nv[1]), on));
  };
  // A Passport moved back to no Purpose drops it, as it drops an option the purpose lacks.
  const set = (...nv: string[]) => put(nv, done && hasTick(nv[0]));
  const tick = hasTick(a) && <CompleteTick on={done} onToggle={on => put(vals, on)} label={signedOnly ? t('tr_fld_signed') : undefined} />;
  const box: React.CSSProperties = { ...noteBoxStyle, height: 28 };
  const dropStyle = (v: string): React.CSSProperties => ({ ...box, cursor: 'pointer', color: v ? 'var(--text-primary)' : 'var(--text-tertiary)' });
  // The Year is typed, not picked (Calem wrote "year - dropdown" for Balance Sheet, "year" here): a
  // permit carries its Buddhist-era year, 304-2561, which the Balance Sheet's list of years doesn't hold.
  if (kind === 'permit') return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <input value={a} onChange={e => set(e.target.value, b, c)} placeholder={t('tr_fld_permit_no')} maxLength={30} autoComplete="off" style={{ ...box, flex: '0 0 84px', minWidth: 0 }} />
      <span style={{ fontSize: 12, color: 'var(--text-tertiary)', flexShrink: 0 }} aria-hidden>-</span>
      <input value={b} onChange={e => set(a, e.target.value.replace(/\D/g, ''), c)} placeholder={t('tr_fld_permit_year')} maxLength={4} inputMode="numeric" autoComplete="off" style={{ ...box, flex: '0 0 52px', minWidth: 0 }} />
      <input value={c} onChange={e => set(a, b, e.target.value)} placeholder={t('tr_fld_issued_to')} maxLength={150} autoComplete="off" style={{ ...box, flex: 1, minWidth: 0 }} />
      {tick}
    </div>
  );
  // The Price narrows to 110px when the Purpose follows the THB. VAT, WHT and Accounting add the Bills
  // card's YYYY-MM + Company line under it; a purpose without that line clears the two. Cash (Land
  // Office Fees) and Cashier's Cheque draw the Price and THB alone, as purposeShown is never theirs.
  if (kind === 'cash' || kind === 'price') return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input value={a} onChange={e => { priceTyped.current = true; set(groupPrice(e.target.value), b, c, d); }}
          onBlur={() => { if (priceTyped.current && padPrice(a) !== a) set(padPrice(a), b, c, d); }}
          placeholder={t('tr_fld_price')} maxLength={60} autoComplete="off" style={{ ...box, flex: purposeShown ? '0 0 110px' : 1, minWidth: 0 }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', flexShrink: 0 }}>THB</span>
        {purposeShown && (
          <select value={b} onChange={e => { const keep = CASH_MONTHLY.includes(e.target.value); set(a, e.target.value, keep ? c : '', keep ? d : ''); }}
            style={{ ...dropStyle(b), flex: 1, minWidth: 0 }}>
            <option value="">{t('tr_fld_cash_purpose')}</option>
            {withValue(TR_CASH_PURPOSES, b).map(x => <option key={x} value={x}>{x}</option>)}
          </select>
        )}
      </div>
      {purposeShown && CASH_MONTHLY.includes(b) && (
        <div style={{ display: 'flex', gap: 6 }}>
          <div style={{ flex: '0 0 100px' }}><YearMonthInput value={c} onChange={v => set(a, b, v, d)} box={box} /></div>
          <div style={{ flex: 1, minWidth: 0 }}><ContactCompanyPicker value={d} companies={companies} onPick={v => set(a, b, c, v)} box={box} /></div>
        </div>
      )}
    </div>
  );
  // The Option list is the picked purpose's — none for 90 Day Report, so the box is greyed out — and a
  // new purpose drops an option it doesn't have. The Re-entry line under them writes to the record's
  // Comments, not to this note (TR_REENTRY).
  if (kind === 'passport') {
    const opts = withValue(passportOptions(a), b);
    const re = reentryIn(comments);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <select value={a} onChange={e => set(e.target.value, passportOptions(e.target.value).includes(b) ? b : '')} style={{ ...dropStyle(a), flex: 1, minWidth: 0 }}>
            <option value="">{t('tr_fld_purpose')}</option>
            {withValue(TR_PASSPORT.map(([p]) => p), a).map(x => <option key={x} value={x}>{x}</option>)}
          </select>
          <select value={b} onChange={e => set(a, e.target.value)} disabled={!opts.length}
            style={{ ...dropStyle(b), flex: '0 0 110px', cursor: opts.length ? 'pointer' : 'default', opacity: opts.length ? 1 : 0.6 }}>
            <option value="">{t('tr_fld_option')}</option>
            {opts.map(x => <option key={x} value={x}>{x}</option>)}
          </select>
          {tick}
        </div>
        <select value={re} onChange={e => { const v = e.target.value; setComments(cm => withReentry(cm, v)); }} style={dropStyle(re)}>
          <option value="">{t('tr_fld_reentry')}</option>
          {TR_REENTRY.map(([v, k]) => <option key={v} value={v}>{t(k)}</option>)}
        </select>
      </div>
    );
  }
  if (kind === 'deed') return (
    <div style={{ display: 'flex', gap: 6 }}>
      <select value={a} onChange={e => set(e.target.value, b)} style={{ ...dropStyle(a), flex: '0 0 124px' }}>
        <option value="">{t('tr_fld_deed_type')}</option>
        {withValue(TR_DEEDS, a).map(x => <option key={x} value={x}>{x}</option>)}
      </select>
      <input value={b} onChange={e => set(a, e.target.value)} placeholder={t('tr_fld_deed_no')} maxLength={60} autoComplete="off" style={{ ...box, flex: 1, minWidth: 0 }} />
    </div>
  );
  if (kind === 'bank') return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input value={a} onChange={e => set(e.target.value, b)} placeholder={t('tr_fld_account')} maxLength={60} autoComplete="off" style={{ ...box, flex: 1, minWidth: 0 }} />
      <select value={b} onChange={e => set(a, e.target.value)} style={{ ...dropStyle(b), flex: 1, minWidth: 0 }}>
        <option value="">{t('tr_fld_bank')}</option>
        {withValue(TR_BANKS, b).map(x => <option key={x} value={x}>{x}</option>)}
      </select>
    </div>
  );
  // A License: its Number, typed, and the company it is issued to, from Contacts or written in.
  if (kind === 'license') return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input value={a} onChange={e => set(e.target.value, b)} placeholder={t('tr_fld_permit_no')} maxLength={60} autoComplete="off" style={{ ...box, flex: 1, minWidth: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}><ContactCompanyPicker value={b} companies={companies} onPick={v => set(a, v)} box={box} /></div>
      {tick}
    </div>
  );
  // Social Security Registration: the company it is for, across the card, then the Signed tick (report
  // 85ddfdbc, 7th opening) and the Complete tick. Each keeps the other as it is.
  if (kind === 'company') return (
    <div style={{ display: 'flex', gap: 6 }}>
      <div style={{ flex: 1, minWidth: 0 }}><ContactCompanyPicker value={a} companies={companies} onPick={v => set(v)} box={box} /></div>
      <CompleteTick on={signed} onToggle={sig => put(vals, done, sig)} label={t('tr_fld_signed')} />
      {tick}
    </div>
  );
  // Client Accounts (ALL) and Company Certificate: the company whose accounts or certificate they are,
  // across the card, with no tick (reports 6a550a3c and f8a05a69).
  if (kind === 'firm') return (
    <div style={{ display: 'flex', gap: 6 }}>
      <div style={{ flex: 1, minWidth: 0 }}><ContactCompanyPicker value={a} companies={companies} onPick={v => set(v)} box={box} /></div>
    </div>
  );
  // Company Documents: the company, then the Issue Date, no tick (report f8a05a69). The date box is
  // DocStorage's day-first one — a native date box shows the browser's own order, month-first on Calem's.
  if (kind === 'firmdate') return (
    <div style={{ display: 'flex', gap: 6 }}>
      <div style={{ flex: 1, minWidth: 0 }}><ContactCompanyPicker value={a} companies={companies} onPick={v => set(v, b)} box={box} /></div>
      <div style={{ flex: '0 0 120px' }}><DayFirstDateInput value={b} onChange={v => set(a, v)} placeholder={t('tr_fld_issue_date')} style={box} /></div>
    </div>
  );
  // Work Permit Photos and Passport Photos: how many, in one short box that takes digits only. A note
  // typed before the box with words in it ('2 + whose') shows whole and is kept as it was until somebody
  // types in the box, which leaves only its digits.
  if (kind === 'number') return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input value={a} onChange={e => set(e.target.value.replace(/\D/g, ''))} placeholder={t('tr_fld_photo_count')} maxLength={3} inputMode="numeric" autoComplete="off" style={{ ...box, flex: '0 0 84px', minWidth: 0 }} />
    </div>
  );
  // ID Card and House Book: the Name across the card, then the Relationship and the Signed tick.
  if (kind === 'person') return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input value={a} onChange={e => set(e.target.value, b)} placeholder={t('tr_fld_person_name')} maxLength={150} autoComplete="off" style={{ ...box, flex: 1, minWidth: 0 }} />
      <select value={b} onChange={e => set(a, e.target.value)} style={{ ...dropStyle(b), flex: '0 0 120px' }}>
        <option value="">{t('tr_fld_relation')}</option>
        {TR_RELATIONS.map(x => <option key={x} value={x}>{x}</option>)}
      </select>
      {tick}
    </div>
  );
  // Director Transfer and Share Transfer: the Name, the company, then the Signed tick (report 478cfa98).
  // The Name takes 120 characters, so a long one with a long company and its '(Signed)' still fits the
  // 300 a note keeps.
  if (kind === 'transfer') return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input value={a} onChange={e => set(e.target.value, b)} placeholder={t('tr_fld_person_name')} maxLength={120} autoComplete="off" style={{ ...box, flex: 1, minWidth: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}><ContactCompanyPicker value={b} companies={companies} onPick={v => set(a, v)} box={box} /></div>
      {tick}
    </div>
  );
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {kind === 'year' ? (
        <select value={a} onChange={e => set(e.target.value, b)} style={{ ...dropStyle(a), flex: '0 0 84px' }}>
          <option value="">{t('tr_fld_year')}</option>
          {withValue(bsYears(), a).map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      ) : (
        <div style={{ flex: '0 0 100px' }}><YearMonthInput value={a} onChange={v => set(v, b)} box={box} /></div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}><ContactCompanyPicker value={b} companies={companies} onPick={v => set(a, v)} box={box} /></div>
    </div>
  );
}

// The Work Permit card: an optional Purpose dropdown of Renew, Update and Cancel in place of its free
// note (Calem, report efcace02, 2nd opening), with the Complete tick after it only once one of the
// three is picked. Saved as the word, 'Renew', or 'Renew (Complete)' when ticked; a stored one is found
// in any case. A note typed before the dropdown — a permit number and holder — opens whole, a status
// tail and all, as an extra entry, so an item saved without touching the box keeps exactly what was
// recorded; the blank entry clears the note. The Passport card's Work Permit option is TR_PASSPORT's.
const TR_WORK_PERMIT: string[] = ['Renew', 'Update', 'Cancel'];
const workPermitOf = (s: string): string => TR_WORK_PERMIT.find(p => p.toLowerCase() === s.trim().toLowerCase()) || '';
function WorkPermitPurposeRow({ note, onChange }: { note: string; onChange: (note: string) => void }) {
  const { t } = useTranslation();
  const [done, setDone] = useState(() => COMPLETE_TAIL.test(note) && !!workPermitOf(note.replace(COMPLETE_TAIL, '')));
  const [val, setVal] = useState(() => { const n = done ? note.replace(COMPLETE_TAIL, '') : note; return workPermitOf(n) || n.trim(); });
  const put = (v: string, on: boolean) => { setVal(v); setDone(on); onChange(withComplete(v, on)); };
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {/* Moving off the three — to blank or the old note — drops the tick, as a Passport's purpose does. */}
      <select value={val} onChange={e => put(e.target.value, done && !!workPermitOf(e.target.value))}
        style={{ ...noteBoxStyle, height: 28, flex: 1, minWidth: 0, cursor: 'pointer', color: val ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
        <option value="">{t('tr_fld_purpose')}</option>
        {withValue(TR_WORK_PERMIT, val).map(x => <option key={x} value={x}>{x}</option>)}
      </select>
      {!!workPermitOf(val) && <CompleteTick on={done} onToggle={on => put(val, on)} />}
    </div>
  );
}

// The Medical Certificate card: an optional dropdown of the two test sets in place of its free note
// (Calem, report efcace02, 3rd opening) — '6 x Diseases Tested (WP)' or '4 x Diseases Tested (Other)',
// saved as written and found in any case. As on the Work Permit card, a note typed before the dropdown
// opens whole as an extra entry, so an item saved without touching the box keeps exactly what was
// recorded, and the blank entry clears the note. It has no Complete tick.
const TR_MEDICAL_CERT: string[] = ['6 x Diseases Tested (WP)', '4 x Diseases Tested (Other)'];
const medicalCertOf = (s: string): string => TR_MEDICAL_CERT.find(p => p.toLowerCase() === s.trim().toLowerCase()) || '';
function MedicalCertRow({ note, onChange }: { note: string; onChange: (note: string) => void }) {
  const { t } = useTranslation();
  const val = medicalCertOf(note) || note.trim();
  return (
    <select value={val} onChange={e => onChange(e.target.value)}
      style={{ ...noteBoxStyle, height: 28, cursor: 'pointer', color: val ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
      <option value="">{t('tr_fld_med_tests')}</option>
      {withValue(TR_MEDICAL_CERT, val).map(x => <option key={x} value={x}>{x}</option>)}
    </select>
  );
}


// ── Correspondence on one custody record ─────────────────────────────────────
// The notices the system sent plus everything the counterparty wrote back, resolved server-side by
// Message-ID chain. Opening it clears the "they replied" flag for the whole team.
function MailPanel({ entry, onClose, onSeen }: { entry: TakenReturnEntry; onClose: () => void; onSeen: () => void }) {
  const { t } = useTranslation();
  const [mails, setMails] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState<string>('');
  const [seenAt, setSeenAt] = useState(0);
  const [linkQ, setLinkQ] = useState('');
  const [hits, setHits] = useState<any[]>([]);
  const [linking, setLinking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d: any = await api.takenReturnMails(entry.id);
      setMails(d.mails || []); setSeenAt(d.seenAt || 0);
    } catch (e: any) { setErr(e.message); } finally { setLoading(false); }
  };
  useEffect(() => {
    load().then(() => { api.takenReturnMailsSeen(entry.id).then(onSeen).catch(() => {}); });
  }, [entry.id]);   // eslint-disable-line

  const search = async (q: string) => {
    setLinkQ(q);
    if (q.trim().length < 2) { setHits([]); return; }
    try { const d: any = await api.takenReturnMailSearch(q.trim()); setHits(d.rows || []); } catch { setHits([]); }
  };
  const link = async (messageId: string) => {
    setLinking(true);
    try { await api.takenReturnMailLink(entry.id, messageId); setLinkQ(''); setHits([]); await load(); }
    catch (e: any) { setErr(e.message); } finally { setLinking(false); }
  };
  const unlink = async (messageId: string) => {
    try { await api.takenReturnMailUnlink(entry.id, messageId); await load(); } catch (e: any) { setErr(e.message); }
  };

  const who = displayCompanyName(`${entry.client_first} ${entry.client_last}`);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 60, display: 'flex', justifyContent: 'flex-end' }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: 760, maxWidth: '96vw', background: 'var(--bg-surface)', height: '100%', overflowY: 'auto', padding: 20, boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <div style={{ fontSize: 16, fontWeight: 800 }}>{t('tr_mails_title')}</div>
          <button onClick={onClose} style={{ marginLeft: 'auto', border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: 'var(--text-tertiary)' }}>×</button>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', marginBottom: 14 }}>
          {who} · {fmtTs(entry.ts)} · {(entry.documents || []).join(', ')}
        </div>
        {err && <div style={{ color: '#D0021B', fontSize: 12.5, marginBottom: 10 }}>{err}</div>}

        {/* Pin an email that never came back as a reply */}
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 10, marginBottom: 14 }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>{t('tr_mail_link_title')}</div>
          <input value={linkQ} onChange={e => search(e.target.value)} placeholder={t('tr_mail_link_ph')} style={inputStyle} />
          {!!hits.length && (
            <div style={{ marginTop: 6, maxHeight: 190, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
              {hits.map(h => (
                <div key={h.message_id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 9px', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{h.subject || '(no subject)'}</div>
                    <div style={{ color: 'var(--text-tertiary)' }}>{h.from_email} · {fmtTs(h.date)}</div>
                  </div>
                  <button disabled={linking} onClick={() => link(h.message_id)} style={{ ...btnGhostSm }}>{t('tr_mail_link_add')}</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {loading ? <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>…</div>
          : !mails.length ? <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>{t('tr_mails_empty')}</div>
          : mails.map(m => {
            const inbound = m.direction === 'received';
            const isNew = inbound && m.date > seenAt;
            return (
              <div key={m.message_id} style={{ border: `1px solid ${isNew ? '#F5A3A3' : 'var(--border)'}`, background: inbound ? (isNew ? '#FEF2F2' : 'var(--c-gray-50)') : 'transparent', borderRadius: 'var(--radius-md)', padding: '9px 11px', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', flexWrap: 'wrap' }} onClick={() => setOpen(o => o === m.message_id ? '' : m.message_id)}>
                  <span style={{ fontSize: 10, fontWeight: 800, borderRadius: 5, padding: '1px 6px', color: '#fff', background: inbound ? '#B45309' : '#0F766E', whiteSpace: 'nowrap' }}>
                    {inbound ? t('tr_mail_in') : t('tr_mail_out')}
                  </span>
                  {isNew && <span style={{ fontSize: 10, fontWeight: 800, color: '#D0021B' }}>● {t('tr_mail_new')}</span>}
                  {m.linked === 'manual' && <span title={t('tr_mail_manual')} style={{ fontSize: 10, fontWeight: 700, color: '#6D28D9', background: '#F3F0FF', borderRadius: 5, padding: '1px 5px' }}>{t('tr_mail_manual_short')}</span>}
                  <span style={{ fontSize: 12.5, fontWeight: 600 }}>{m.from_name || m.from_email}</span>
                  <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{m.from_email}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{fmtTs(m.date)}</span>
                </div>
                <div style={{ fontSize: 12.5, marginTop: 3, color: 'var(--text-secondary)' }}>{m.subject}</div>
                {open === m.message_id && (
                  <div style={{ marginTop: 8, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                    <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginBottom: 6 }}>
                      To: {(m.to || []).join(', ')}{m.cc?.length ? ` · Cc: ${m.cc.join(', ')}` : ''}
                    </div>
                    {/* Render the REAL email (owner 2026-08-25: "邮件预览需要和真实的邮件格式一模一样")
                        — the HTML body in the same sandboxed iframe the mail module uses; plain
                        text only as a fallback for bodies that never had HTML. */}
                    {m.body_html
                      ? <HtmlFrame html={m.body_html} />
                      : <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.55 }}>{m.body_text || '(no text body)'}</div>}
                    {m.linked === 'manual' && <button onClick={() => unlink(m.message_id)} style={{ ...btnGhostSm, marginTop: 8, color: '#D0021B' }}>{t('tr_mail_unlink')}</button>}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}

// ── Add / edit / delete form (modal) ─────────────────────────────────────────
// One physical document on the form: its type, its note, and the required Original/Electronic
// choice ('' until the operator picks — saving is blocked while any row is undecided). `src` ties a
// row back to the chain-outstanding entry it was ticked from (return-picking, owner 2026-08-25).
type FormDocItem = { uid: string; type: string; note: string; form: '' | 'original' | 'electronic'; kept: boolean; src?: string };

// Direction-aware display of a document type (owner 2026-08-26): on a RETURN leg "(from Land
// Office)" reads as "(to Land Office)" — display ONLY. The stored name stays canonical ("from"),
// or chain auto-linking and the outstanding-item matching would stop recognising the same document.

// A couple of document names read differently depending on which way the document is travelling.
// Taking documents IN for notarisation, they are not notarised yet — that is what the client is
// handing over and what we should call it on the form and in the register; the leg that hands them
// BACK is the one carrying notarised documents (owner 2026-09-16).
// ⛔ DISPLAY ONLY. The stored name stays canonical, exactly as the counterparty suffix rule below:
// chain auto-linking and the outstanding-item matching key on the stored name, and 137 live records
// already carry it — rename the data and a return stops recognising what was taken.
// Items the firm KEEPS rather than holds. Mirrors services/takenReturnMatch.ts keptByDefault() —
// money handed over and photos are consumed, they never come back, so they must not sit in the
// custody ledger as something we owe the client (owner 2026-09-18).
// ⛔ The marriage certificate is deliberately absent: it can be a copy we keep or an original we
// must return ("it will vary depending on the document" — Calem), so the operator ticks that one.
const keptByDefault = (n: string): boolean =>
  /^\s*(cash\b|cheque\b|cashier|scan\s*\(payment)/i.test(n) || /\bphotos?\b/i.test(n);

const DIRECTIONAL_DOC_LABEL: Record<string, string> = {
  'Notarized Documents': 'Documents (for Notarization)',
};
// A catalogue entry that has been renamed reads under its new name whichever way it travels: the card
// headed Chanote became Title Deed in report fcc6685f's 6th opening, Chanote being one of the types in
// its dropdown. ⛔ Display only, like the rule above — the items saved as 'Chanote' keep that name, and
// a return ticked off the chain's list copies it, so it still matches what was taken.
// Director Transfer (Signed) and Share Transfer (Signed) lost their '(Signed)' in report 478cfa98, the
// card's Signed tick saying it instead. That tick reads the note, so on their old items it starts unticked.
const RENAMED_DOC_LABEL: Record<string, string> = {
  'Chanote': 'Title Deed',
  'Director Transfer (Signed)': 'Director Transfer', 'Share Transfer (Signed)': 'Share Transfer',
};
const formerNames = (type: string): string[] => Object.keys(RENAMED_DOC_LABEL).filter(k => RENAMED_DOC_LABEL[k] === type);
const docLabel = (type: string, dir: 'taken' | 'returned' | string): string =>
  (dir === 'taken' ? DIRECTIONAL_DOC_LABEL[String(type)] : '') || RENAMED_DOC_LABEL[String(type)] || String(type);

// A leg's physical items — doc_items when the record has them, else the legacy fields exploded the
// same way the edit form seeds them (one row per type; scan_documents ⇒ electronic).
const legItemsOf = (e: any): { type: string; note: string; original: boolean; kept: boolean }[] =>
  (Array.isArray(e?.doc_items) && e.doc_items.length)
    ? e.doc_items.map((i: any) => ({ type: String(i.type || ''), note: String(i.note || ''), original: !!i.original, kept: !!i.kept }))
    : (e?.documents || []).map((d: string) => ({ type: d, note: String(((e?.doc_notes || {}) as any)[d] || ''), original: !(e?.scan_documents || []).includes(d), kept: keptByDefault(d) }));

// The subject of a chain, cut back to the gist. Everything from the first " - " onward goes:
// the chain already says who the client is and which documents are involved, so the label only has
// to answer "what is this about" — "Non IMM (Business) Extension", not "… - 11 Months" (owner
// 2026-09-16: a custody chain needs a reference to the job, not its full description). The complete
// title is still on hover, and the Jobs list keeps its full titles — there the term and the property
// name are part of the quoted work.
const purposeOf = (s: string): string => {
  let t = String(s || '').replace(/\s*【\d+】\s*$/, '').trim();
  const i = t.indexOf(' - ');
  if (i > 0) t = t.slice(0, i).trim();
  // Cutting inside a bracket would leave it hanging ("Due Diligence [Sacarat 3"), so drop the
  // fragment that was never closed.
  const open = (t.match(/[[(]/g) || []).length, close = (t.match(/[\])]/g) || []).length;
  if (open > close) t = t.replace(/\s*[[(][^[\]()]*$/, '').trim();
  return t;
};

// What a chain appears to be ABOUT when no Job is linked to it: the note the legs repeat. A status
// tail ("(Complete)") is the same subject, so it is folded in; a note that is only an amount
// ("400 THB") describes a payment, not the matter, so it is skipped. Falls back to the document
// types when the notes say nothing useful. ID Card and House Book's "(Signed)" is a status tail too,
// and Social Security Registration's two, "(Signed) (Complete)", come off together (report 85ddfdbc,
// 7th opening).
const NOTE_STATUS = /(?:\s*\((?:complete|completed|done|signed)\.?\))+\s*$/i;
const MONEY_ONLY = /^[\d.,\s]+(?:thb|baht)?$/i;
// A note that is ONLY a status word is not what the matter is about — the same reasoning as the
// "(Complete)" tail above, just without the brackets. Volodymyr Tereshko's notarisation chain was
// titled "Emailed" because somebody noted that on the returned copy (owner 2026-09-18).
const STATUS_ONLY = /^(?:emailed?|e-?mail|sent|signed|done|complete[d]?|collected|received|returned|scanned?|copy|copies|ok|paid|pending|posted|delivered)\.?$/i;
// Pick the entry that appears most often; ties go to whichever showed up first.
const topOf = (tally: Map<string, { n: number; first: number }>): string =>
  Array.from(tally.entries()).sort((a, b) => (b[1].n - a[1].n) || (a[1].first - b[1].first))[0][0];

function noteSubject(group: any[]): string {
  // ── THE ITEM NAMES THE CHAIN, not the note on it (owner 2026-09-19) ──────────────────────────
  // "For any chain that does not have a job assigned, please use the PRINCIPAL ITEM SELECTED as
  // the name for the chain." Pariya Srinaen's chain is four legs of one Bungalow Key, and it was
  // titled "for Cleaning" — the note somebody wrote on the second leg — because the note vote below
  // used to run first. A note says why the key moved once; the key is what the chain is about.
  //
  // Principal = the item type carried by the most legs. With a single item throughout (the common
  // case) that is simply the item, which is also how the owner first put it on 2026-09-18: "use the
  // first item under document taken". Shown in the taken direction, as it reads on the leg itself.
  //
  // Kept items are skipped here whenever there is a real document to weigh instead — a chain is
  // never "about" the cash that paid for it. Without that, Susan Angenent's property-tax chain
  // would be titled "Cash (Payment to SLF)".
  const items = new Map<string, { n: number; first: number }>();
  const kept = new Map<string, { n: number; first: number }>();
  const tallyLegs = (dir: 'taken' | 'returned') => group.forEach((leg, li) => {
    if (leg.type !== dir) return;
    legItemsOf(leg).forEach(it => {
      const ty = String(it.type || '').trim();
      if (!ty) return;
      const tally = it.kept ? kept : items;
      const cur = tally.get(ty);
      if (cur) cur.n++; else tally.set(ty, { n: 1, first: li });
    });
  });
  tallyLegs('taken');
  // A chain with NO taken leg is still named after its item: the return legs are weighed the same
  // way instead (owner 2026-09-26, Calem: "As per the naming protocol these two should be renamed
  // to house book and bank book"). Nualchan Sawadchai's and Mariia Zhikhareva's chains are Document
  // Return legs only, so they went straight to the note vote below and were titled "21/22" and
  // "Kasikorn Bank" — notes typed on the legs — instead of House Book and Bank Book. The label stays
  // in the taken direction, so an item names a chain the same whichever legs the chain happens to
  // have. Whatever the taken legs name still wins, so chains that have one keep their names.
  if (!items.size && !kept.size) tallyLegs('returned');
  if (items.size) return docLabel(topOf(items), 'taken');
  // A chain made up ENTIRELY of kept items — cash, a cheque, photos, and nothing else ever taken —
  // has no real document to weigh against, so the exclusion above doesn't apply: it names itself
  // after the kept item instead of falling through to whatever note/amount text was typed on it
  // (owner 2026-09-22, Calem: "the name of this chain should be cash (Payment to SLF)"). Chains
  // that mix a real document with a cash item are unaffected — they still resolve above, from the
  // document.
  if (kept.size) return docLabel(topOf(kept), 'taken');

  // Only when no leg, taken or returned, names an item does the note speak.
  // A status tail ("(Complete)") is the same subject so it is folded in; a note that is only an
  // amount describes a payment, not the matter.
  const tally = new Map<string, { n: number; first: number }>();
  group.forEach((leg, li) => legItemsOf(leg).forEach(it => {
    const s = String(it.note || '').replace(NOTE_STATUS, '').trim();
    if (!s || MONEY_ONLY.test(s) || STATUS_ONLY.test(s)) return;
    const cur = tally.get(s);
    if (cur) cur.n++; else tally.set(s, { n: 1, first: li });
  }));
  if (tally.size) return purposeOf(topOf(tally));

  const types = Array.from(new Set(group.flatMap(l => legItemsOf(l).map(i => i.type)).filter(Boolean)));
  return types.slice(0, 2).join(' · ') + (types.length > 2 ? ` +${types.length - 2}` : '');
}

// What the chain still OWES back: replay the legs chronologically — taken legs add their items,
// return legs consume them (matched by type+flag, falling back to type). Used by the return-picking
// tick list; counts on the module strip use the count columns instead (exact for legacy records).
function chainOutstanding(chain: any[]): { key: string; type: string; note: string; original: boolean; kept: boolean }[] {
  const held: { key: string; type: string; note: string; original: boolean; kept: boolean }[] = [];
  for (const leg of [...chain].sort((a, b) => (a.ts - b.ts) || (a.created_at - b.created_at))) {
    // A kept item (a payment, photos) never enters the outstanding list — there is nothing to tick
    // back to the client, and offering it invited exactly the confusion this fixes.
    if (leg.type === 'taken') legItemsOf(leg).filter(i => !i.kept).forEach((i, k) => held.push({ key: `${leg.id}:${k}`, ...i }));
    else for (const r of legItemsOf(leg)) {
      let ix = held.findIndex(h => h.type === r.type && h.original === !!r.original);
      if (ix < 0) ix = held.findIndex(h => h.type === r.type);
      if (ix >= 0) held.splice(ix, 1);
    }
  }
  return held;
}

// Chain status for the module strip — read off the LEDGER, never off the last leg (owner
// 2026-09-16). "The last leg was a return to the client" is not the same question as "is anything
// still out", and in production the two disagreed on TEN chains that all read "All returned":
//   • Caroline Milne — Wedding Vows went back to her at 10:20, so the chain closed. Her passport
//     had gone to Immigration at 09:43 and was still there. A hand-over to a government office is
//     a change of address, not a discharge: we still owe it to the owner.
//   • Tania Carroll — the passport came home on 14/09 and closed the chain, while a medical
//     certificate and six photos taken on 28/08 had never left our office at all.
//   • David Rustica — 17 items taken, 8 returned; the 8th return closed a chain owing 9.
// So: settled ⇔ the books balance. Nothing in our hands AND nothing parked with a third party.
function groupStatus(group: TakenReturnEntry[]) {
  const n = (v: any) => Number(v) || 0;
  const isThird = (e: TakenReturnEntry) => e.counterparty === 'third_party';
  const sum = (f: (e: TakenReturnEntry) => number) => group.reduce((a, e) => a + f(e), 0);
  // In our hands: a taking adds, a hand-over subtracts — whoever it went to.
  // Custody is what must be handed BACK: total minus the items the firm keeps. Same expression as
  // the server's RET (routes/taken_return.ts) — two copies of this sum have drifted before.
  const ret = (e: TakenReturnEntry) => Math.max(0, n(e.count_total) - n((e as any).count_kept));
  const held = Math.max(0, sum(e => (e.type === 'taken' ? ret(e) : -ret(e))));
  const heldOrig = Math.max(0, sum(e => (e.type === 'taken' ? n(e.count_originals) : -n(e.count_originals))));
  // Parked OUT THERE — and only a government department counts (owner 2026-09-16). Handing the
  // passport to Sophie, the third-party individual acting for the owner, discharges custody exactly
  // as handing it to the owner does; handing it to Immigration does not, because we put it there
  // and it is still ours to get back. An unrecorded kind counts as a department: a custody register
  // must fail towards "still outstanding", never towards "nothing to see here".
  // Note the firm frequently RECEIVES documents from a third party it never sent (an agent's
  // monthly invoices), which drives this negative — clamped, because that means nothing is parked.
  const parked = (e: TakenReturnEntry) => isThird(e) && String((e as any).third_party_kind || '') !== 'individual';
  const atThirdN = Math.max(0, sum(e => (parked(e) ? (e.type === 'returned' ? ret(e) : -ret(e)) : 0)));
  const closed = held === 0 && atThirdN === 0;
  const atThird = atThirdN > 0;
  // Whoever is holding it now — the most recent department we sent something to.
  const lastThird = [...group].reverse().find(e => e.type === 'returned' && parked(e));
  const pending = group.some(g => g.approval_status === 'pending');
  const days = Math.max(0, Math.floor((Date.now() / 1000 - n(group[0].ts)) / 86400));
  return { closed, atThird, atThirdN, held, heldOrig, pending, days, thirdName: atThird ? String(lastThird?.third_party_name || '') : '' };
}

/**
 * Every job linked anywhere on the chain, one entry per job. A link CONFIRMED on any leg speaks for
 * the whole chain: a six-leg chain linked by hand on leg 1, with the proposal then landing on legs
 * 2-6, must not keep asking about a job somebody already chose.
 */
function chainJobsOf(group: TakenReturnEntry[]): any[] {
  const jobMap = new Map<string, any>();
  for (const j of group.flatMap(e => ((e as any).jobs || []) as any[])) {
    const prev = jobMap.get(j.id);
    if (!prev || (prev.pending && !j.pending)) jobMap.set(j.id, j);
  }
  return Array.from(jobMap.values());
}

/** Is the work behind this chain over? Nothing linked counts as over — the owner's call. */
function workDoneOf(group: TakenReturnEntry[]): boolean {
  const js = chainJobsOf(group);
  return js.length === 0 || js.every(j => !!j.done);
}

function EntryForm({ entry, chainHead, linkSource, chain, firms, defaultCompany, onClose, onSaved }: {
  entry: TakenReturnEntry | null; chainHead?: boolean; linkSource?: TakenReturnEntry | null; chain?: TakenReturnEntry[]; firms: Firm[]; defaultCompany: string | null; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isNew = !entry;
  // when adding a linked record, seed from the source (same item) and flip the direction
  const seed = entry || linkSource || null;

  const [type, setType] = useState<'taken' | 'returned'>(
    entry?.type || (linkSource ? (linkSource.type === 'taken' ? 'returned' : 'taken') : 'taken'));
  // Owner 2026-08-25: Company starts EMPTY on a new record (shown as "-") and must be picked —
  // no more silently inheriting the operator's firm. Edit/linked records keep their saved company.
  const [companyId, setCompanyId] = useState<string>(seed?.company_id || '');
  const [first, setFirst] = useState(seed?.client_first || '');
  const [last, setLast] = useState(seed?.client_last || '');
  const [email, setEmail] = useState(seed?.client_email || '');
  const [ts, setTs] = useState<string>(tsToLocalInput(entry?.ts || Math.floor(Date.now() / 1000)));
  // Per-item document model (owner 2026-08-25): the checkbox set could not say "TWO passports" —
  // now every + click adds one PHYSICAL document as its own row, with its own note and a REQUIRED
  // Original / Electronic·scan choice (starts empty; the form refuses to save until it is made).
  // A legacy record explodes into one row per type (note from doc_notes, flag from scan_documents);
  // its Total may shrink on resave if it counted duplicates the old model could not name — the
  // operator adds the duplicates back with +, and the material-change warning surfaces the shift.
  // What the chain still owes back — the tick list a linked RETURN starts from (owner 2026-08-25).
  const outstanding = React.useMemo(
    () => (!entry && linkSource ? chainOutstanding(chain && chain.length ? chain : [linkSource]) : []),
    []);  // eslint-disable-line react-hooks/exhaustive-deps
  const seedItems = (): FormDocItem[] => {
    const src: any = seed;
    if (!src) return [];
    // A return created from the chain's + starts from the OUTSTANDING items, all ticked — the
    // common case is a full return; untick what stays with the firm.
    if (!entry && linkSource && linkSource.type === 'taken')
      return outstanding.map((o, k) => ({ uid: `o${k}`, type: o.type, note: o.note, form: o.original ? 'original' as const : 'electronic' as const, kept: false, src: o.key }));
    if (Array.isArray(src.doc_items) && src.doc_items.length)
      return src.doc_items.map((i: any, k: number) => ({ uid: `s${k}`, type: String(i.type || ''), note: String(i.note || ''), form: i.original ? 'original' as const : 'electronic' as const, kept: !!i.kept }));
    const notes = (src.doc_notes || {}) as Record<string, string>;
    const scans = new Set<string>(src.scan_documents || []);
    return (src.documents || []).map((d: string, k: number) => ({ uid: `l${k}`, type: d, note: notes[d] || '', form: scans.has(d) ? 'electronic' as const : 'original' as const, kept: keptByDefault(d) }));
  };
  const [items, setItems] = useState<FormDocItem[]>(seedItems);
  const uidRef = useRef(1000);
  // Every company in Contacts, for the Company box of a Balance Sheet, Bills or License item, or of a
  // payment for VAT, WHT or Accounting — asked for once, the first time such an item is on the form.
  // A Social Security Registration item's Company box is the same list (report fcc6685f, 10th opening),
  // and so is a Client Accounts (ALL) item's (report 6a550a3c), and a Company Certificate's or Company
  // Documents' (report f8a05a69), and a Director Transfer's or Share Transfer's (report 478cfa98).
  const [contactCompanies, setContactCompanies] = useState<string[] | null>(null);
  const companiesAsked = useRef(false);
  const needCompanies = items.some(i => ['year', 'month', 'license', 'company', 'firm', 'firmdate', 'transfer'].includes(noteKindOf(i.type) || '')
    || (noteKindOf(i.type) === 'cash' && CASH_MONTHLY.includes(cashParts(i.note, true)[1])));
  useEffect(() => {
    if (!needCompanies || companiesAsked.current) return;
    companiesAsked.current = true;
    api.contactCompanies().then(r => setContactCompanies(r?.companies || [])).catch(() => setContactCompanies([]));
  }, [needCompanies]);
  // Nothing on the record but Cash, Cheque or Scan (Payment to SLF): on a taken record their cards ask
  // what the payment is for (Calem, report 85ddfdbc).
  const paymentOnly = items.every(i => !i.type.trim() || noteKindOf(i.type) === 'cash');
  const [other, setOther] = useState('');
  const [comments, setComments] = useState(entry?.comments || '');
  // Counterparty of THIS leg: the document's owner (client) or a third party (Immigration, Land Office…).
  // Owner 2026-08-25: on a NEW record (fresh or +linked) the counterparty starts EMPTY and must be
  // chosen — no more silently defaulting to Client. An edit keeps the record's saved value.
  const [counterparty, setCounterparty] = useState<'client' | 'third_party' | ''>(
    entry ? (entry.counterparty || (entry.third_party_email ? 'third_party' : 'client')) : '');
  const [thirdName, setThirdName] = useState(entry?.third_party_name || '');
  // A blank kind on an EXISTING leg must read as DEPARTMENT (owner 2026-09-16). The ledger counts
  // "not individual" as parked, so the 35 legacy legs with no kind are what hold their chains open —
  // and this form defaulted the other way, so saving an unrelated typo on an Immigration leg wrote
  // 'individual' and settled the chain silently, passport still at Immigration. New legs keep the
  // old default: there the operator is looking at the radio and choosing.
  const [thirdKind, setThirdKind] = useState<'individual' | 'department'>(
    entry ? ((entry as any).third_party_kind === 'individual' ? 'individual' : 'department') : 'individual');
  // Email the client a custody receipt. ON when creating. On an edit it starts OFF — but any change
  // to what the notice ASSERTS (type, documents, counts, which are electronic, whose documents they
  // are) turns it back ON by itself, because leaving it off would leave the client holding a receipt
  // that contradicts the firm's own record (owner 2026-08-08). Comments and the timestamp do not.
  // Calem 2026-09-03 (Baurina): an edit made BEFORE the client was ever notified must not default
  // the switch off — re-arming approval then wiped the initial notice and it went office-only.
  const [notifyClient, setNotifyClient] = useState(isNew || !Number((entry as any)?.notified_at || 0));
  const notifyTouched = React.useRef(false);
  const [thirdEmail, setThirdEmail] = useState(entry?.third_party_email || '');
  const [password, setPassword] = useState('');
  const [docSearch, setDocSearch] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [approvalUrl, setApprovalUrl] = useState('');
  const [savedPending, setSavedPending] = useState(false);   // saved & awaiting approval — the dialog shows for everyone; the raw link only for approvers
  const [approvalCopied, setApprovalCopied] = useState(false);

  // An internal office mailbox is NOT a client email — used as a placeholder in the past, it made the
  // "client receipt" go to the office instead of the customer. Guard the input + auto-fill the real one.
  const isOfficeEmail = isOfficeAddr;
  // On name blur, if the email is blank or an office placeholder, pull the client's last real email.
  const maybeSuggestEmail = async (f: string, l: string) => {
    if (!f.trim() && !l.trim()) return;
    if (email.trim() && !isOfficeEmail(email)) return;   // keep a genuine typed address
    try { const r: any = await api.suggestTakenReturnEmail(f.trim(), l.trim()); if (r?.email) setEmail(r.email); } catch { /* best-effort */ }
  };

  // Pre-ticked for the types that can never be an original we owe back; everything else starts
  // unticked so a keepable-or-returnable document (a certificate) is a conscious choice.
  const addItem = (type: string) => setItems(prev => [...prev, { uid: `n${uidRef.current++}`, type, note: '', form: '', kept: keptByDefault(type) }]);
  const rmItem = (uid: string) => setItems(prev => prev.filter(i => i.uid !== uid));
  const updItem = (uid: string, patch: Partial<FormDocItem>) => setItems(prev => prev.map(i => (i.uid === uid ? { ...i, ...patch } : i)));
  // A hand-typed "Passport (to Immigration)" would put the counterparty back into the document name
  // the catalogue no longer carries it in (owner 2026-08-29) — so a trailing "(to X)" / "(from X)"
  // is stripped as it is added. "(Payment to SLF)" and the like survive: the parenthesis has to
  // START with to/from to be a counterparty.
  const stripCounterparty = (n: string) => n.replace(/\s*\(\s*(?:to|from)\s+[^)]*\)\s*$/i, '').trim();
  const addOther = () => {
    const names = other.split(',').map(x => stripCounterparty(x.trim())).filter(Boolean);
    if (!names.length) return;
    names.forEach(addItem); setOther('');
  };
  // The legacy record fields as this form would save them — counts are READ-ONLY derivations now
  // (owner 2026-08-25: "自动出现…只读"), and material-change detection compares on this level.
  const derivedDocs = Array.from(new Set(items.map(i => i.type)));
  const derivedOriginals = items.filter(i => i.form === 'original').length;
  const derivedKept = items.filter(i => i.kept).length;
  const derivedScans = Array.from(new Set(items.filter(i => i.form === 'electronic').map(i => i.type)));
  const derivedNotes: Record<string, string> = {};
  for (const i of items) if (i.note.trim()) derivedNotes[i.type] = derivedNotes[i.type] ? `${derivedNotes[i.type]} | ${i.note.trim()}` : i.note.trim();
  // Optional email — but if filled it must LOOK like an email (owner 2026-08-25); a typo here means
  // the custody notice silently goes nowhere.
  const emailValid = !email.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
  const outBtn: React.CSSProperties = { border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', borderRadius: 6, padding: '2px 9px', fontSize: 11, fontWeight: 700, cursor: 'pointer' };

  // What the notice states, as the form currently stands vs. as the record was loaded.
  const sameSet = (a: string[], b: string[]) => a.length === b.length && [...a].sort().join('\u0000') === [...b].sort().join('\u0000');
  const materialChanged = !isNew && !!entry && (
    type !== entry.type
    || !sameSet(derivedDocs, entry.documents || [])
    || items.length !== Number(entry.count_total || 0)
    || derivedOriginals !== Number(entry.count_originals || 0)
    || !sameSet(derivedScans, entry.scan_documents || [])
    || first.trim() !== (entry.client_first || '')
    || last.trim() !== (entry.client_last || '')
    || counterparty !== (entry.counterparty || 'client')
    || JSON.stringify(Object.fromEntries(Object.entries(derivedNotes).sort()))
       !== JSON.stringify(Object.fromEntries(Object.entries(((entry as any).doc_notes || {}) as Record<string, string>).sort()))
  );
  useEffect(() => {
    if (isNew || notifyTouched.current) return;   // never override a deliberate choice
    setNotifyClient(materialChanged);
  }, [materialChanged, isNew]);

  // ── Post-approval lock (owner 2026-08-30) ────────────────────────────────────────────────────
  // An approved record is read-only until someone unlocks it. Only Liang, Calem and Nick may unlock
  // (Calem, report 85ddfdbc #8); the server refuses anyone else, so they get no Unlock button — a
  // refused call would page the admins. Unlocking sends nothing; it is the SAVE that re-opens
  // approval and emails the approver.
  const mayUnlock = !!useStore(s => s.me?.mayApprovePeakDoc);
  const [unlocked, setUnlocked] = useState(false);
  const locked = !!(entry as any)?.locked && !unlocked;
  const [unlocking, setUnlocking] = useState(false);
  const doUnlock = async () => {
    if (!entry) return;
    if (!window.confirm(t('tr_unlock_confirm'))) return;
    setUnlocking(true);
    try { await api.takenReturnUnlock(entry.id); setUnlocked(true); }
    catch (e: any) { window.alert(e.message); }
    finally { setUnlocking(false); }
  };

  const save = async () => {
    if (!counterparty) { setErr(t('tr_err_counterparty')); return; }
    // The document names no longer say who the leg is with (owner 2026-08-29), so the third party
    // box is the ONLY record of it — an unnamed third party would lose the information entirely.
    if (counterparty === 'third_party' && !thirdName.trim()) { setErr(t(thirdKind === 'department' ? 'tr_err_third_dept' : 'tr_err_third_name')); return; }
    if (!companyId) { setErr(t('tr_err_company')); return; }
    if (!first.trim() && !last.trim()) { setErr(t('tr_err_name')); return; }
    if (items.length === 0) { setErr(t('tr_err_docs')); return; }
    // Every physical document must state what it IS — the custody receipt hangs on it. The counts
    // are derived from these choices, so an undecided row would silently misstate the originals.
    if (items.some(i => !i.form)) { setErr(t('tr_err_doc_form')); return; }
    if (!emailValid) { setErr(t('tr_err_email')); return; }
    if (isNew && !password) { setErr(t('tr_err_password')); return; }
    setSaving(true); setErr('');
    const isThird = counterparty === 'third_party';
    const payload: any = {
      type, companyId, clientFirst: first.trim(), clientLast: last.trim(), clientEmail: email.trim(),
      // docItems is authoritative; the legacy fields ride along so a not-yet-redeployed backend
      // (fe/be deploys are separate) still stores a consistent record.
      ts: localInputToTs(ts),
      docItems: items.map(i => ({ type: i.type, note: i.note.trim(), original: i.form === 'original', kept: !!i.kept })),
      documents: derivedDocs, countTotal: items.length, countOriginals: derivedOriginals, countKept: derivedKept,
      scanDocuments: derivedScans, docNotes: derivedNotes,
      comments: comments.trim(),
      counterparty, thirdPartyName: isThird ? thirdName.trim() : '',
      thirdPartyKind: isThird ? thirdKind : '',
      thirdPartyEmail: isThird ? thirdEmail.trim() : '',
      // Owner 2026-08-31: on an individual third-party leg the CLIENT is the recipient and the
      // third party is copied — so the client switch now applies to every kind of leg.
      notifyClient,
      password,
      linkTo: linkSource?.id,   // optional manual chaining; otherwise the server auto-links
    };
    try {
      const r = isNew ? await api.createTakenReturn(payload) : await api.updateTakenReturn(entry!.id, payload);
      if (r?.emailWarning) window.alert(r.emailWarning);
      onSaved();
      // Approval gate (owner 2026-08-25): the save no longer notifies anyone — hand the operator
      // the approval link right here so they can pass it to the approver.
      if (r?.approvalUrl !== undefined) {
        setApprovalUrl(r.approvalUrl || '');
        setSavedPending(true);
        if (r.approvalUrl) { try { await navigator.clipboard.writeText(r.approvalUrl); setApprovalCopied(true); } catch { /* manual copy */ } }
      } else onClose();
    } catch (e: any) { setErr(e.message); } finally { setSaving(false); }
  };

  const lockBanner = locked ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 14px', margin: '0 0 14px', background: '#FFFBEB', border: '1px solid #FBD38D', borderRadius: 10 }}>
      <i className="ti ti-lock" style={{ color: '#B45309', fontSize: 16 }} />
      <span style={{ fontSize: 12.5, color: '#92400E', fontWeight: 700 }}>{t('tr_locked_title')}</span>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)', flex: 1, minWidth: 200 }}>{t(mayUnlock ? 'tr_locked_hint' : 'tr_locked_hint_others')}</span>
    </div>
  ) : null;

  if (savedPending) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }}>
        <div style={{ width: 480, maxWidth: '94vw', background: 'var(--bg-surface)', borderRadius: 14, border: '1px solid var(--border)', padding: 24, boxShadow: 'var(--shadow-md)' }}>
          <div style={{ fontSize: 15.5, fontWeight: 800, color: '#92400E', marginBottom: 8 }}><i className="ti ti-lock" style={{ marginRight: 7 }} />{t('tr_saved_pending_title')}</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55, marginBottom: 12 }}>{t('tr_saved_pending')}</div>
          {approvalUrl && <div style={{ display: 'flex', gap: 6, alignItems: 'center', background: 'var(--bg-app)', border: '1px solid var(--border-strong)', borderRadius: 9, padding: '8px 10px', marginBottom: 14 }}>
            <span style={{ flex: 1, fontSize: 11.5, fontFamily: 'ui-monospace, monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{approvalUrl}</span>
            <button onClick={async () => { try { await navigator.clipboard.writeText(approvalUrl); setApprovalCopied(true); } catch { /* manual */ } }}
              style={{ border: 'none', borderRadius: 7, background: approvalCopied ? '#1D9E75' : '#B45309', color: '#fff', fontWeight: 800, fontSize: 11.5, padding: '6px 12px', cursor: 'pointer', flexShrink: 0 }}>
              {approvalCopied ? t('qo_appr_copied') : t('qo_appr_copy')}
            </button>
          </div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{t('cal_due_ok')}</button>
          </div>
        </div>
      </div>
    );
  }

  const del = async () => {
    if (!entry || !window.confirm(t('tr_confirm_delete'))) return;
    setSaving(true);
    try { await api.deleteTakenReturn(entry.id); onSaved(); onClose(); }
    catch (e: any) { setErr(e.message); setSaving(false); }
  };

  // An entry is still found by a name it had before ("chanote" finds Title Deed).
  const docQ = docSearch.trim().toLowerCase();
  const visibleDocs = docQ
    ? DOC_TYPES.filter(d => [d, ...formerNames(d)].some(n => n.toLowerCase().includes(docQ)))
    : DOC_TYPES;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 880, maxWidth: '96vw', maxHeight: '94vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', padding: '18px 22px', boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: linkSource ? 6 : 12 }}>
          {isNew ? t('tr_add') : t('edit')}
        </div>
        {lockBanner}
        {linkSource && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--accent)', background: 'var(--bg-active)', borderRadius: 8, padding: '7px 11px', marginBottom: 16 }}>
            <i className="ti ti-link" />{t('tr_linked_to')}: <b>{`${linkSource.client_first} ${linkSource.client_last}`.trim()}</b> · {linkSource.documents.join(', ')}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
          {/* Type + counterparty on one row (owner 2026-08-25: wider + more compact dialog).
              A FRESH record can only be a TAKEN — a return needs an existing taken leg, so
              Document Return is only offered when editing or when the record is created from a
              chain's + (linkSource). The counterparty is a required radio pair that starts EMPTY. */}
          <div style={{ display: 'flex', gap: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{t('tr_type')} *</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {((
                  // A fresh record and the FIRST leg of an existing chain can only be TAKEN (owner
                  // 2026-08-25) — a legacy head that already IS a return keeps both buttons so it
                  // can at least be corrected.
                  (isNew && !linkSource) || (!!entry && chainHead && entry.type === 'taken') ? ['taken'] : ['taken', 'returned']
                ) as ('taken' | 'returned')[]).map(v => (
                  <button key={v} onClick={() => setType(v)} style={{
                    flex: 1, padding: '9px', borderRadius: 'var(--radius-md)', border: '1px solid',
                    borderColor: type === v ? (v === 'taken' ? '#E24B4A' : '#1D9E75') : 'var(--border-strong)',
                    background: type === v ? (v === 'taken' ? '#E24B4A' : '#1D9E75') : 'transparent',
                    color: type === v ? '#fff' : 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}>
                    <i className={`ti ti-${v === 'taken' ? 'circle-arrow-down' : 'circle-arrow-up'}`} style={{ marginRight: 6 }} aria-hidden />
                    {v === 'taken' ? t('tr_taken') : t('tr_returned')}
                  </button>
                ))}
              </div>
              {((isNew && !linkSource) || (!!entry && chainHead && entry.type === 'taken')) && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 5 }}>{t('tr_new_taken_only')}</div>}
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{type === 'taken' ? t('tr_cp_from') : t('tr_cp_to')} <span style={{ color: '#DC2626' }}>*</span></label>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', minHeight: 38, padding: '0 2px' }}>
                {(['client', 'third_party'] as const).map(v => (
                  <label key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: counterparty === v ? 'var(--accent)' : 'var(--text-secondary)' }}>
                    <input type="radio" name="tr-counterparty" checked={counterparty === v} onChange={() => setCounterparty(v)} />
                    <i className={`ti ti-${v === 'client' ? 'user' : 'building-bank'}`} aria-hidden />
                    {v === 'client' ? t('tr_cp_client') : t('tr_cp_third')}
                  </label>
                ))}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 5 }}>{t('tr_cp_hint')}</div>
            </div>
          </div>

          {/* Third party details — shown when this leg is with a third party (e.g. Immigration). */}
          {counterparty === 'third_party' && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 12, display: 'flex', flexDirection: 'column', gap: 10, background: 'var(--bg-app)' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}><i className="ti ti-building-bank" style={{ marginRight: 6 }} aria-hidden />{t('tr_third_block')}</div>
              {/* Person or department? The choice decides who may ever be emailed about this leg. */}
              <div style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
                {(['individual', 'department'] as const).map(k => (
                  <label key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: thirdKind === k ? 'var(--accent)' : 'var(--text-secondary)' }}>
                    <input type="radio" name="tr-third-kind" checked={thirdKind === k}
                      onChange={() => { setThirdKind(k); setThirdName(''); if (k === 'department') setThirdEmail(''); }} />
                    <i className={`ti ti-${k === 'individual' ? 'user' : 'building-bank'}`} aria-hidden />
                    {k === 'individual' ? t('tr_third_individual') : t('tr_third_department')}
                  </label>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>{thirdKind === 'department' ? t('tr_third_department') : t('tr_third_name')} <span style={{ color: '#DC2626' }}>*</span></label>
                  {thirdKind === 'department' ? (
                    <select value={thirdName} onChange={e => setThirdName(e.target.value)} style={inputStyle}>
                      <option value="">—</option>
                      {TR_DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  ) : (
                    <input value={thirdName} onChange={e => setThirdName(e.target.value)} placeholder={t('tr_third_name_ph')} style={inputStyle} />
                  )}
                </div>
                {/* A department leg is internal-only: no address is asked for, because none is used. */}
                {thirdKind === 'individual' && (
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>{t('tr_third_email')}</label>
                    <input value={thirdEmail} onChange={e => setThirdEmail(e.target.value)} placeholder="somchai@example.com" style={inputStyle} />
                  </div>
                )}
              </div>
              <div style={{ fontSize: 11.5, color: thirdKind === 'department' ? '#1D4ED8' : '#0E7A64', background: thirdKind === 'department' ? '#EFF6FF' : '#ECFDF5', border: `1px solid ${thirdKind === 'department' ? '#BFDBFE' : '#A7F3D0'}`, borderRadius: 8, padding: '7px 10px' }}>
                <i className={`ti ti-${thirdKind === 'department' ? 'lock' : 'mail'}`} style={{ marginRight: 5 }} aria-hidden />
                {t(thirdKind === 'department' ? 'tr_dept_internal' : 'tr_indiv_cc')}
              </div>
            </div>
          )}

          {/* Company + Timestamp */}
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{t('tr_company')} <span style={{ color: '#DC2626' }}>*</span></label>
              <select value={companyId} onChange={e => setCompanyId(e.target.value)} style={inputStyle}>
                <option value="">-</option>
                {firms.map(f => <option key={f.id} value={f.id}>{firmLabel(f)}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{t('tr_timestamp')}</label>
              <input type="datetime-local" value={ts} onChange={e => setTs(e.target.value)} style={inputStyle} />
            </div>
          </div>

          {/* Client name */}
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{t('tr_first')} *</label>
              <input value={first} onChange={e => setFirst(e.target.value)} onBlur={() => maybeSuggestEmail(first, last)} style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{t('tr_last')}</label>
              <input value={last} onChange={e => setLast(e.target.value)} onBlur={() => maybeSuggestEmail(first, last)} style={inputStyle} />
            </div>
          </div>
          {/* Hidden for a department leg — nothing is ever emailed to the client about one, so
              asking for the address only invites a mistake (Calem 2026-08-31). */}
          <div style={{ display: counterparty === 'third_party' && thirdKind === 'department' ? 'none' : undefined }}>
            <label style={labelStyle}>{t('tr_email')}</label>
            <input value={email} onChange={e => setEmail(e.target.value)} placeholder="example@company.com"
              style={{ ...inputStyle, ...(emailValid ? {} : { border: '1px solid #F5A3A3', background: '#FEF2F2' }) }} />
            {!emailValid && (
              <div style={{ fontSize: 11.5, fontWeight: 700, color: '#B42318', marginTop: 4 }}>
                <i className="ti ti-alert-circle" style={{ marginRight: 4 }} aria-hidden />{t('tr_err_email')}
              </div>
            )}
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('tr_owner_hint')}</div>
            {counterparty === 'client' && isOfficeEmail(email) && (
              <div style={{ fontSize: 11.5, color: '#B45309', background: '#FEF3C7', borderRadius: 6, padding: '6px 9px', marginTop: 6 }}>
                <i className="ti ti-alert-triangle" style={{ marginRight: 5 }} />{t('tr_office_email_warn')}
              </div>
            )}
            {/* Email the client a receipt of what was taken/returned (office cc'd). */}
            {counterparty === 'client' && email.trim() && !isOfficeEmail(email) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                <button type="button" onClick={() => { notifyTouched.current = true; setNotifyClient(v => !v); }} style={{ width: 46, height: 24, borderRadius: 999, border: 'none', background: notifyClient ? 'var(--accent)' : 'var(--c-gray-200)', position: 'relative', cursor: 'pointer', flexShrink: 0 }}>
                  <span style={{ position: 'absolute', top: 2, left: notifyClient ? 24 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
                </button>
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('tr_notify_client_hint')}</span>
              </div>
            )}
            {materialChanged && counterparty === 'client' && (
              <div style={{ marginTop: 8, border: '1px solid #F5A3A3', background: '#FEF2F2', color: '#B42318', borderRadius: 'var(--radius-md)', padding: '8px 10px', fontSize: 12.5, fontWeight: 600, lineHeight: 1.5 }}>
                <i className="ti ti-alert-triangle-filled" style={{ marginRight: 6 }} aria-hidden />{t('tr_material_warn')}
              </div>
            )}
          </div>

          {/* Documents — per-item picker (owner 2026-08-25): + on a type adds ONE physical document
              to the right-hand list; each added row carries its own note and a REQUIRED
              Original / Electronic·scan choice. The same type can be added as many times as there
              are physical copies. */}
          <div>
            <div style={{ marginBottom: 6 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>{type === 'taken' ? t('tr_docs_taken') : t('tr_docs_returned')} * <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>({items.length})</span></label>
            </div>
            {!entry && linkSource && type === 'returned' && outstanding.length > 0 && (
              <div style={{ border: '1px solid #B6E9EC', background: '#F0FBFC', borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <span style={{ fontSize: 11.5, fontWeight: 800, color: '#0A8A94' }}>{t('tr_out_title').replace('{n}', String(outstanding.length))}</span>
                  <span style={{ flex: 1 }} />
                  <button type="button" style={outBtn}
                    onClick={() => setItems(prev => [...prev.filter(i => !i.src), ...outstanding.map(o => ({ uid: `n${uidRef.current++}`, type: o.type, note: o.note, form: (o.original ? 'original' : 'electronic') as FormDocItem['form'], kept: false, src: o.key }))])}>
                    {t('tr_all')}
                  </button>
                  <button type="button" style={outBtn} onClick={() => setItems(prev => prev.filter(i => !i.src))}>{t('tr_out_none')}</button>
                </div>
                {outstanding.map(o => {
                  const on = items.some(i => i.src === o.key);
                  return (
                    <label key={o.key} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, padding: '2px 0', cursor: 'pointer', color: 'var(--text-primary)', minWidth: 0 }}>
                      <input type="checkbox" checked={on}
                        onChange={() => setItems(prev => on ? prev.filter(i => i.src !== o.key) : [...prev, { uid: `n${uidRef.current++}`, type: o.type, note: o.note, form: (o.original ? 'original' : 'electronic') as FormDocItem['form'], kept: false, src: o.key }])} />
                      {/* The colon rides on the name, as in the Documents column: these are flex items 7px
                          apart, so a colon opening the note would stand 7px off the badge (report fcc6685f, 8th opening). */}
                      <span style={{ fontWeight: 600, flexShrink: 0 }}>{docLabel(o.type, type)}{o.note && ':'}</span>
                      <span style={{ fontSize: 10, fontWeight: 800, color: o.original ? '#166534' : '#6D28D9', background: o.original ? '#E8F6EE' : '#F3F0FF', borderRadius: 5, padding: '0 6px', flexShrink: 0 }}>{o.original ? t('tr_form_original') : t('tr_form_electronic')}</span>
                      {o.note && <span style={{ color: 'var(--text-tertiary)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shownNote(o.type, o.note)}</span>}
                    </label>
                  );
                })}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
              {/* Left: search (with clear ✕) + the catalogue — one row per type, + adds an instance */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <div style={{ position: 'relative', marginBottom: 6 }}>
                  <input value={docSearch} onChange={e => setDocSearch(e.target.value)} placeholder={t('tr_doc_search')} name="tr-doc-filter" autoComplete="off"
                    style={{ width: '100%', boxSizing: 'border-box', padding: '6px 28px 6px 9px', fontSize: 12, borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-app)', color: 'var(--text-primary)', outline: 'none' }} />
                  {docSearch !== '' && (
                    <button type="button" onClick={() => setDocSearch('')} title={t('tr_doc_filter_clear')}
                      style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer', padding: 2, color: 'var(--text-tertiary)', display: 'inline-flex' }}>
                      <i className="ti ti-x" style={{ fontSize: 13 }} />
                    </button>
                  )}
                </div>
                <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '6px 8px' }}>
                  {visibleDocs.map(d => {
                    // Counted by the name the cards show, so an item saved as Chanote counts under Title Deed.
                    const n = items.filter(i => docLabel(i.type, type) === docLabel(d, type)).length;
                    return (
                      <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0', minWidth: 0 }}>
                        <button type="button" onClick={() => addItem(d)} title={t('tr_doc_add_one')}
                          style={{ width: 20, height: 20, borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--accent)', fontWeight: 800, fontSize: 14, lineHeight: 1, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>+</button>
                        <span onClick={() => addItem(d)} style={{ fontSize: 12.5, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}>{docLabel(d, type)}</span>
                        {n > 0 && <span style={{ fontSize: 10.5, fontWeight: 800, color: '#166534', background: '#E8F6EE', border: '1px solid #9BD8B3', borderRadius: 999, padding: '0 6px', flexShrink: 0 }}>×{n}</span>}
                      </div>
                    );
                  })}
                  {visibleDocs.length === 0 && (
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)', padding: 6 }}>
                      {docSearch.trim() ? (
                        <button type="button" onClick={() => setDocSearch('')}
                          style={{ border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', borderRadius: 7, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                          <i className="ti ti-filter-off" style={{ marginRight: 5 }} />{t('tr_doc_filter_clear')}
                        </button>
                      ) : '—'}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <input value={other} onChange={e => setOther(e.target.value)} placeholder={t('tr_other')} name="tr-doc-other" autoComplete="off"
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addOther(); } }}
                    style={{ ...inputStyle, flex: 1 }} />
                  <button type="button" onClick={addOther} disabled={!other.trim()}
                    style={{ padding: '0 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: other.trim() ? 'var(--accent)' : 'var(--c-gray-200)', color: '#fff', fontWeight: 800, fontSize: 13, cursor: other.trim() ? 'pointer' : 'default', flexShrink: 0 }}>+</button>
                </div>
              </div>
              {/* Right: the added documents — one card per physical item, with a pink caption so the
                  operator knows this side IS the picked set (owner 2026-08-25) */}
              <div style={{ flex: 1.15, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: 11.5, fontWeight: 800, color: '#DB2777', marginBottom: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <i className="ti ti-files" style={{ marginRight: 5 }} aria-hidden />{type === 'taken' ? t('tr_added_pink') : t('tr_added_pink_ret')}
                </div>
                <div style={{ flex: 1, minHeight: 120, maxHeight: 330, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 8, background: 'var(--bg-app)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {items.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-tertiary)', padding: 8, lineHeight: 1.5 }}>{t('tr_items_empty')}</div>}
                {items.map(it => (
                  <div key={it.uid} style={{ border: `1px solid ${it.form ? 'var(--border)' : '#F0C36D'}`, background: 'var(--bg-surface)', borderRadius: 8, padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{docLabel(it.type, type)}</span>
                      <button type="button" onClick={() => rmItem(it.uid)} title={t('delete_short')}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, color: 'var(--text-tertiary)', display: 'inline-flex', flexShrink: 0 }}>
                        <i className="ti ti-x" style={{ fontSize: 14 }} />
                      </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      {([['original', t('tr_form_original')], ['electronic', t('tr_form_electronic')]] as const).map(([v, lbl]) => (
                        <label key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: it.form === v ? (v === 'original' ? '#166534' : '#6D28D9') : 'var(--text-secondary)', cursor: 'pointer' }}>
                          <input type="radio" name={`trform-${it.uid}`} checked={it.form === v} onChange={() => updItem(it.uid, { form: v })} />
                          {lbl}
                        </label>
                      ))}
                      {!it.form && <span style={{ fontSize: 10.5, fontWeight: 700, color: '#9A6700' }}>{t('tr_form_required')}</span>}
                      {/* "We keep this" — a payment, photos, a copy that is never handed back. It
                          still appears on the client's receipt; it simply stops counting as
                          something we owe them (owner 2026-09-18). */}
                      <label title={t('tr_kept_tip')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, marginLeft: 'auto', cursor: 'pointer', color: it.kept ? '#B45309' : 'var(--text-secondary)' }}>
                        <input type="checkbox" checked={!!it.kept} onChange={e => updItem(it.uid, { kept: e.target.checked })} />
                        {t('tr_kept')}
                      </label>
                    </div>
                    {/* Balance Sheet, the three bank papers, Bills, Invoices & Receipts, Building Permit,
                        Cash, Cheque and Scan (Payment to SLF), Title Deed (Chanote until the 6th
                        opening), Passport, every License, Social Security Registration, Work Permit
                        Photos and Passport Photos, ID Card and House Book, Client Accounts (ALL),
                        Company Certificate and Company Documents, Director Transfer and Share Transfer, and
                        Cash (Land Office Fees) and Cashier's Cheque: boxes that fill the note (Calem,
                        reports fcc6685f, 01a9b1a2, efcace02, 0bacef33, 85ddfdbc, 0bcbe9ec, 6a550a3c,
                        6deefcce, f8a05a69 and 478cfa98);
                        Work Permit: an optional Purpose
                        dropdown — Renew, Update or Cancel — with the Complete tick once one is picked
                        (reports 53a19ac8 and efcace02, 2nd opening); Medical Certificate: an optional
                        dropdown of the two test sets, no tick (report efcace02, 3rd opening); every
                        other type: the free note. */}
                    {noteKindOf(it.type)
                      ? <NoteFieldsRow kind={noteKindOf(it.type)!} note={it.note} companies={contactCompanies} recType={type} paymentOnly={paymentOnly}
                          comments={comments} setComments={setComments} onChange={note => updItem(it.uid, { note })} />
                      : it.type === 'Work Permit'
                        ? <WorkPermitPurposeRow note={it.note} onChange={note => updItem(it.uid, { note })} />
                        : it.type === 'Medical Certificate'
                          ? <MedicalCertRow note={it.note} onChange={note => updItem(it.uid, { note })} />
                          : <input value={it.note} onChange={e => updItem(it.uid, { note: e.target.value })} maxLength={300}
                              placeholder={docNoteHint(it.type) || t('tr_doc_note_ph')}
                              style={noteBoxStyle} />}
                  </div>
                ))}
                </div>
              </div>
            </div>
          </div>

          {/* Counts — derived from the items, read-only (owner 2026-08-25: "自动出现…只读") */}
          {items.length > 0 && (
            <div style={{ display: 'flex', gap: 10 }}>
              {/* Same 1 : 1.15 split as the two panels above, so the boxes line up edge-to-edge. */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={labelStyle}>{t('tr_count_total')}</label>
                <input value={String(items.length)} readOnly tabIndex={-1} style={{ ...inputStyle, background: 'var(--c-gray-100)', color: 'var(--text-secondary)', cursor: 'default' }} />
              </div>
              <div style={{ flex: 1.15, minWidth: 0 }}>
                <label style={labelStyle}>{t('tr_count_originals')}</label>
                <input value={String(derivedOriginals)} readOnly tabIndex={-1} style={{ ...inputStyle, background: 'var(--c-gray-100)', color: 'var(--text-secondary)', cursor: 'default' }} />
              </div>
            </div>
          )}

          {/* Comments */}
          <div>
            <label style={labelStyle}>{t('tr_comments')}</label>
            <textarea value={comments} onChange={e => setComments(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
          </div>

          {/* Password confirm — only when creating */}
          {isNew && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
              <label style={labelStyle}>{t('tr_password')} *</label>
              <div style={{ fontSize: 12, color: 'var(--accent)', marginBottom: 6 }}>{t('tr_password_hint')}</div>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} style={inputStyle} />
            </div>
          )}

          {!isNew && (
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', borderTop: '1px solid var(--border)', paddingTop: 10, lineHeight: 1.6 }}>
              <div>{t('tr_recorded')}: {entry!.created_by_name || '—'} · {fmtDateTime(entry!.created_at)}</div>
              <div>{t('tr_last_edit')}: {entry!.updated_by_name || '—'} · {fmtDateTime(entry!.updated_at)}</div>
            </div>
          )}
          {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)' }}>{err}</div>}

          <div style={{ display: 'flex', alignItems: 'center', marginTop: 4 }}>
            {!isNew && (
              <button onClick={del} disabled={saving} style={{ padding: '8px 14px', borderRadius: 'var(--radius-md)', border: 'none', background: 'transparent', color: '#E24B4A', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                <i className="ti ti-trash" style={{ fontSize: 14, marginRight: 6 }} aria-hidden />{t('delete')}
              </button>
            )}
            <div style={{ flex: 1 }} />
            <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginRight: 8 }}>{t('cancel')}</button>
            {locked ? (mayUnlock && (
              <button onClick={doUnlock} disabled={unlocking} style={{ padding: '8px 16px', borderRadius: 'var(--radius-md)', border: '1px solid #B45309', background: 'transparent', color: '#B45309', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                <i className="ti ti-lock-open" style={{ marginRight: 6 }} />{unlocking ? t('login_wait') : t('tr_unlock')}
              </button>
            )) : (
              <button onClick={save} disabled={saving} style={{ padding: '8px 16px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{saving ? t('login_wait') : t('tr_save_approve')}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Reusable view — used by BOTH /m/taken_return and the Portal Dashboard tab ──
export function TakenReturnView() {
  const { t } = useTranslation();
  const me = useStore(s => s.me);
  const [rows, setRows] = useState<TakenReturnEntry[]>([]);
  const [firms, setFirms] = useState<Firm[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  // The toolbar's company pills (All · Accounting · Legal · General) are gone (Calem, Bugify #870a98b0 — crossed
  // out), so the list always asks for every company. `firms` stays: it fills the form's Company picker.
  const company = 'all';
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  // Chain-status filter (owner 2026-08-25): all / open (firm holds or at third party) / closed
  // (returned to the client) / awaiting approval. Applied server-side, chain-level.
  const { badgeOnly, clearBadge } = useBadgeOnly();
  const [isApprover, setIsApprover] = useState(false);
  // Badge drill-down lands straight on the awaiting-approval view (owner 2026-08-31) — the same
  // rows the red number counts. Clearing the bar goes back to All.
  const [statusF, setStatusF] = useState<'all' | 'open' | 'closed' | 'pending'>(badgeOnly ? 'pending' : 'all');
  useEffect(() => { if (badgeOnly) setStatusF('pending'); }, [badgeOnly]);
  const [editing, setEditing] = useState<TakenReturnEntry | null>(null);
  // Owner 2026-08-25: is the record being edited the FIRST leg of its chain? A chain head can only
  // ever be a TAKEN — the edit form hides "Document Return" for it.
  const [editHead, setEditHead] = useState(false);
  const [linkSource, setLinkSource] = useState<TakenReturnEntry | null>(null);
  const [linkChain, setLinkChain] = useState<TakenReturnEntry[] | null>(null);   // the + button's whole chain
  // Which FOLDED chains the operator has opened this session. Only chains that qualify to fold are
  // ever in here, so nothing is remembered for a chain that is always open anyway.
  const [openChains, setOpenChains] = useState<Set<string>>(new Set());
  const [showForm, setShowForm] = useState(false);
  const [err, setErr] = useState('');
  const [resending, setResending] = useState('');
  const [mailFor, setMailFor] = useState<TakenReturnEntry | null>(null);
  const showToast = useStore(st => st.showToast);
  // ── Re-chain with the mouse (owner 2026-08-29) ────────────────────────────
  // Auto-linking is strict on purpose, so the operator must be able to correct it: drag a record
  // onto any record of another chain to join it, or onto the bar at the bottom to pull it out.
  const [chainEmailFor, setChainEmailFor] = useState<string | null>(null);   // dispute email: the whole chain
  const [jobLinkFor, setJobLinkFor] = useState<TakenReturnEntry | null>(null);   // Job-link picker target
  // Deep link from the approval page's "open & edit in the system" (owner 2026-08-29). The record
  // is fetched by id rather than hunted for in the list — it may be on any page of it.
  const [deepParams] = useSearchParams();
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropOn, setDropOn] = useState<string | null>(null);   // row currently hovered while dragging
  const rechain = async (srcId: string, body: { joinId?: string; groupId?: string }) => {
    setDragId(null); setDropOn(null);
    if (!srcId) return;
    try { const r: any = await api.takenReturnSetGroup(srcId, body); if (!r?.unchanged) { await load(); showToast(t('tr_rechained')); } }
    catch (ex: any) { setErr(ex.message); }
  };

  // Send the counterparty a corrected notice built from the record as it stands NOW. Always
  // confirmed first: this is outbound mail to a client, never something the list does by itself.
  const resendNotice = async (e: any) => {
    const who = `${e.client_first || ''} ${e.client_last || ''}`.trim() || 'the counterparty';
    const to = e.counterparty === 'client' ? (e.client_email || t('tr_resend_office')) : (e.third_party_email || t('tr_resend_office'));
    if (!window.confirm(t('tr_resend_confirm').replace('{who}', who).replace('{to}', to))) return;
    setResending(e.id); setErr('');
    try { const r: any = await api.resendTakenReturnNotice(e.id); await load(); showToast(t('tr_resend_done').replace('{to}', r.to || to)); }
    catch (ex: any) { setErr(ex.message); }
    finally { setResending(''); }
  };

  const [pageSize, setPageSize] = useState(getPageSize());
  // How many chains each band holds, counted server-side over the WHOLE result — the divider
  // must be able to say "312" on a page that was only sent 50 of them.
  const [bands, setBands] = useState<{ open: number; done: number }>({ open: 0, done: 0 });
  const load = async (p = page, comp = company, qq = q, st = statusF) => {
    setLoading(true); setErr('');
    try {
      const res = await api.getTakenReturn({ page: p, company: comp, q: qq.trim(), status: st === 'all' ? '' : st, limit: pageSize });
      // The outstanding band only ships with page 1, so a chain that becomes outstanding while the
      // operator is deeper in the archive can empty the page under them — "No records yet." with
      // forty open chains one click away. Fall back to page 1 rather than show that (2026-09-16).
      if (!res.rows.length && p > 1) { setLoading(false); return load(1, comp, qq, st); }
      setRows(res.rows); setTotal(res.total); setPage(res.page); setFirms(res.companies || []); setIsApprover(!!(res as any).isApprover);
      setBands((res as any).bands || { open: 0, done: 0 });
    } catch (e: any) { setErr(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(1, company); }, [company, statusF, pageSize]); // eslint-disable-line
  useEffect(() => {
    const id = deepParams.get('open');
    if (!id) return;
    api.takenReturnOne(id).then((d: any) => {
      if (!d?.row) return;
      setEditing(d.row); setEditHead(!d.row.group_id); setLinkSource(null); setShowForm(true);
    }).catch((e: any) => setErr(e.message));
  }, [deepParams]);   // eslint-disable-line
  // Search is server-side now (the old client filter only saw the current page) — debounced.
  // Skip the mount run — it double-loaded the list on open (owner 2026-09-06).
  const qInit = useRef(true);
  useEffect(() => { if (qInit.current) { qInit.current = false; return; } const h = setTimeout(() => load(1, company), 250); return () => clearTimeout(h); }, [q]); // eslint-disable-line

  const filtered = rows;   // filtering happens server-side (q + status), whole chains preserved

  // zIndex: positioned content later in the page paints over an un-indexed sticky header, so the
  // header claims the top. It came in while each chain's status strip was positioned to carry the
  // client name on the frame's edge; the name is back inside the strip (owner 2026-09-23, from
  // Calem), so nothing in the rows is positioned now — the guard costs nothing and stays.
  // The header is the page's white, --bg-surface, as the Jobs lists' header is (Calem, report ddf1ba0e, 3rd opening:
  // "Change every page background to white"), parted from the page under it by its thin line; the edge fills below
  // wear it too, so it still reaches the screen edges. It was the light grey, --c-gray-50 (Calem, report c0d8d666:
  // "Revert to background light gray color for all menus that look similar to this"), and the near-white before that.
  // 11 → 12px (Calem, report 66c9d174, 2nd opening: "Do the same for all other menus in the SLF system") → 13px
  // (Calem, report bcbcb01c, 2nd opening: "1 pt larger text for this menu highlighted in blue"). Every heading in the
  // row wears it, Time Stamp and Taken or Returned too, so the row stays one size. The 1st opening grew File
  // Storage's headings instead; they keep their 13px.
  // Then Active Jobs' headings, which every staff list now wears: black, --text-primary, at the regular weight, where
  // they were the light grey --text-tertiary at semibold (600), on one 16px line, over the darker --border-strong line
  // where it was --border (Calem, report 4bd4ae4e, 2nd opening: "Make all menus in the SLF system look like this: the
  // same height, text color, no bold, horizontal line colors etc."; report bcbcb01c, 3rd opening: "Change the text
  // color to black and remove bold"). The 13px stays; the band is about a pixel shorter, 37px as every list's is.
  const th: React.CSSProperties = { textAlign: 'left', fontSize: 13, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 12px', lineHeight: '16px', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border-strong)', position: 'sticky', top: 0, zIndex: 1, background: 'var(--bg-surface)' };
  // The list container pads the table in 14px from the screen edge (below), so the header row's
  // own box — like every other row's — ends 14px short of the edge too. These sit on the first
  // and last header cell only and paint that 14px strip the header's own colour (owner 2026-09-22,
  // Calem reopen #3), so the coloured header reaches the screen edge without making the row wider
  // than the table's real columns underneath it.
  // They reach a pixel below the cell's inside (bottom −1), as the Peak lists' thEdge does, so their grey line meets
  // the header's and runs unbroken to the screen edges; it sat a pixel above it (Calem, report 66c9d174, 2nd opening:
  // "Make sure to include the horizontal gray line for any pages that scroll"). The line is the headings' darker
  // --border-strong now (report 4bd4ae4e — see th).
  const thEdgeFillL: React.CSSProperties = { position: 'absolute', top: 0, bottom: -1, left: -14, width: 14, background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-strong)' };
  const thEdgeFillR: React.CSSProperties = { position: 'absolute', top: 0, bottom: -1, right: -14, width: 14, background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-strong)' };
  const td: React.CSSProperties = { fontSize: 13, color: 'var(--text-primary)', padding: '10px 12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
  const numTd: React.CSSProperties = { ...td, textAlign: 'right' };
  // Column shares (Calem, Bugify #870a98b0, 4th opening: "Remove 5% from the taken or returned column and 5% from
  // the comments column and give all that space to the Created/Edited column"): Taken or Returned 6% and Comments 12%
  // of the table, where they had no width and took spare room — about 11% and 14% on Calem's 1900px screen — and
  // Created / Edited 6.5%, where it was a fixed 84px (Calem 2026-09-25) that cut staff names at 68px. The 4th opening
  // made Comments 9% and Created / Edited 14.5%; the 5th ("Take 3% from the created / edited column and give it back
  // to the comments column") moved those 3 points back to Comments, and Calem's report 53a19ac8 ("remove 5% of the
  // column space from the Created/Edited column and get back to the Documents column") 5 more to Documents, 24 → 29%.
  // Calem's report 85ddfdbc, 3rd opening ("Reduce the name field by 5% and give it back to the comments field"), gave
  // Comments 5 more, 12 → 17%. The name field is Client, not Created / Edited, which is down to its heading. Client
  // keeps no width, so the 5 points come out of the room shared by the columns without one, Client the widest of them.
  // Its 4th opening ("Remove that extra space from the comments field and give it to the documents field") passed
  // those 5 points on to Documents: Comments is back to 12% and Documents 29 → 34%. The two still take 46% between
  // them, so the columns without a width, Client among them, keep the room the 3rd opening left them.
  // Created / Edited goes no narrower than its heading, which keeps to one line. The other columns share
  // the rest. Taken or Returned goes no narrower than its label and its "from Client (owner)" tag, which keep to one
  // line. A third-party name no longer widens it: it ends in "…" and shows whole on hover, as a staff name too long
  // for Created / Edited does. fitCell does that — the table counts it 0 wide while sizing its columns, then it
  // stretches to its cell.
  const fitCell: React.CSSProperties = { width: 0, minWidth: '100%' };
  const byLine: React.CSSProperties = { ...fitCell, overflow: 'hidden', textOverflow: 'ellipsis' };
  // Record rows wear the Filing list's two colours (Calem, Bugify #e2eb6886: "Make this page's theme similar to the
  // filing page theme in that the colors should be yellow and light brown"): the 1st, 3rd, 5th … row of each chain
  // card is Filing's cream — the top menu's Document amber three shades lighter, see rowTint and DocStorage — and the
  // 2nd, 4th … that cream under Filing's 0.04 black stripe, worked out here as a solid colour (#FEFBF6 → #F4F1EC).
  // Filing's own rows moved on to two shades and a brown stripe (Bugify #4ec27a67, 2nd opening), then back to this
  // cream under a paler brown one, #EEDDC5 (Bugify #f53a87ee, 2nd opening); these stayed.
  // The page's own zebra can't draw it: the rows paint over it, and it counts the strip and spacer rows too. Were the
  // Document amber two shades lighter (Bugify #1a85b4b0), and pale blue #EEF4FF on a linked follow-up, which the
  // "Linked" pill and the ↳ arrow still mark. A drop target's green keeps its colour. Each chain's status strip keeps
  // its state's fill unless a job is linked, which makes it the top menu's Document brown (see stripBg).
  const creamBg = rowTint('doc_storage', 3)!;
  const zebraBg = '#' + [1, 3, 5].map(i => Math.round(parseInt(creamBg.slice(i, i + 2), 16) * 0.96).toString(16).padStart(2, '0')).join('').toUpperCase();

  // The page — round the cards, and the spacer rows and the archive heading between them, which paint it so the zebra
  // can't — is white, --bg-surface, the column headings' own (Calem, report ddf1ba0e, 3rd opening: "Change every page
  // background to white"). It was the app's beige, then for Bugify #b07c8dae's 1st opening the top menu's Document amber
  // one shade lighter, #FBF2E3, and for its 2nd the list's grey, --c-gray-50. The cards keep their cream, inside their
  // brown frame (FR).
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--bg-surface)' }}>
      {badgeOnly && <BadgeFilterBar count={rows.filter(e => e.approval_status === 'pending').length} labelKey="bf_approvals" onClear={() => { clearBadge(); setStatusF('all'); }} />}
      {/* Toolbar: status filter + search + refresh. The light grey, --c-gray-50, as File Storage's toolbar band is and
          every top-menu page's now (Calem, report 3e769579, 4th opening: "Only the top menu strip on every page turns
          that same grey then the rest of the page below this menu has a white background"). It was the page's white,
          --bg-surface; the page under it stays white. Its line is a shade darker, --border-strong where it was --border,
          as is the top menu's over it (Calem, report a903bda5, 3rd opening: "Make the gray horizontal lines one shade
          darker on all page menus like this"). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderBottom: '1px solid var(--border-strong)', background: 'var(--c-gray-50)' }}>
        {/* The status pills wear the top menu's Document box, whose Tracking button this page is (Calem, Bugify
            #667b55cb — see menuPill): the selected one its solid brown #BA7517 with white words, the rest its pale wash
            with brown words and its line. Were #B45309, and white with a grey outline. The company pills and the
            divider that stood in front of them are gone (Bugify #870a98b0, see `company` above). */}
        {([['all', t('tr_all')], ['open', t('tr_f_open')], ['closed', t('tr_f_closed')], ['pending', t('tr_awaiting_approval')]] as const).map(([k, label]) => (
          <button key={`st-${k}`} onClick={() => setStatusF(k)} style={{
            padding: '5px 11px', borderRadius: 999, border: '1px solid', fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
            ...menuPill('taken_return', statusF === k),
          }}>{label}</button>
        ))}
        <div style={{ flex: 1 }} />
        {/* The search box is Filing's (Calem, Bugify #cba01676, 4th opening: "Change the search bubble in tracking to the
            same as filing"), as FileLibrary draws it in Document Storage's toolbar: round ends, 6px 14px and 12px
            semibold, 170px wide, in the Document box's colours from menuPill — the status pills' own, unselected: its pale
            wash, its line and brown words, the placeholder's too (mf-ph-inherit) — with the magnifier 14px in and in the
            brown, the words 32px in, clear of it. It stays at the right-hand end, beside the refresh button. It was an
            8px-cornered box 200px wide with the faint grey outline on the app's beige, --bg-app, its grey magnifier 10px
            in and its words 28px. */}
        <div style={{ position: 'relative' }}>
          <i className="ti ti-search" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: tabColor('taken_return') }} aria-hidden />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('tr_search')} className="mf-ph-inherit" style={{ width: 170, padding: '6px 14px 6px 32px', fontSize: 12, fontWeight: 600, borderRadius: 999, border: '1px solid', outline: 'none', ...menuPill('taken_return', false) }} />
        </div>
        <button onClick={() => load()} title={t('refresh')} style={{ width: 32, height: 32, borderRadius: 'var(--radius-md)', border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>
          <i className="ti ti-refresh" style={{ fontSize: 15 }} aria-hidden />
        </button>
      </div>

      {/* Table */}
      {/* Padding (owner 2026-09-22, from Calem, reopened #3): vertical bumped another 2-3pt over
          pass 2 (5px→8px) now that horizontal (14px) is the larger of the two; left/right
          unchanged. The top half no longer lives on this container — it used to push the sticky
          header down with every other row, leaving a page-coloured gap under the toolbar and
          insetting the header's own background from the screen edges by the same 14px. That gap
          to the first card is now the spacer row at the top of <tbody> below, so the header sits
          flush under the toolbar at every scroll position; thEdgeFillL/R (above) carry its
          background the rest of the way to the screen edges. */}
      <div style={{ flex: 1, overflow: 'auto', padding: '0 14px 8px' }}>
        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '10px 16px' }}>{err}</div>}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><i className="ti ti-loader" style={{ fontSize: 24, color: 'var(--text-tertiary)' }} aria-hidden /></div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead><tr>
              <th style={th}><span aria-hidden style={thEdgeFillL} />{t('tr_timestamp')}</th>
              <th style={th}>{t('tr_client')}</th>
              <th style={{ ...th, width: '6%' }}>{t('tr_type')}</th>
              {/* Owner 2026-09-22, from Calem, reopen #4: Documents' share cut 30%→24% and the
                  Taken/Returned cells pulled toward each other (facing padding 12px→6px) so
                  both count columns sit closer together and shift left, freeing room for
                  Comments — since given its own 12% (see the column shares above byLine).
                  Documents is now 34%: 29% from report 53a19ac8, then the 5 points report
                  85ddfdbc's 3rd opening had given Comments (Calem, its 4th opening: "Remove
                  that extra space from the comments field and give it to the documents field"). */}
              <th style={{ ...th, width: '34%' }}>{t('tr_documents')}</th>
              <th style={{ ...th, textAlign: 'right', paddingRight: 6 }}>{t('tr_col_taken_c')}</th>
              <th style={{ ...th, textAlign: 'right', paddingLeft: 6 }}>{t('tr_col_return_c')}</th>
              <th style={{ ...th, width: '12%' }}>{t('tr_comments')}</th>
              <th style={th}>{t('tr_col_jobs')}</th>
              <th style={{ ...th, padding: '10px 8px', width: '6.5%' }}>{t('tr_col_by')}</th>
              <th style={th}><span aria-hidden style={thEdgeFillR} /></th>
            </tr></thead>
            <tbody>
              {/* Replaces this container's old padding-top (owner 2026-09-22, Calem reopen #3) —
                  the gap between the now edge-to-edge header and the first card, kept off the
                  header itself so the header can sit flush under the toolbar. */}
              {filtered.length > 0 && <tr aria-hidden="true"><td colSpan={10} style={{ padding: 0, height: 8, border: 'none', background: 'var(--bg-surface)' }} /></tr>}
              {(() => {
                // group records that belong to the same chain (group_id); ungrouped = singletons
                const map = new Map<string, TakenReturnEntry[]>();
                for (const e of filtered) {
                  const key = e.group_id ? `g:${e.group_id}` : `s:${e.id}`;
                  if (!map.has(key)) map.set(key, []);
                  map.get(key)!.push(e);
                }
                const groups = Array.from(map.values()).map(rs => rs.slice().sort((a, b) => (a.ts - b.ts) || (a.created_at - b.created_at) || (a.id < b.id ? -1 : 1)));
                // Two bands, newest first inside each, mirroring the server's ORDER BY exactly
                // (owner 2026-09-16). The rows ARRIVE in that order and must render in it, or the
                // page walks one sequence while the pager pages another.
                //
                // The band itself comes FROM the server, so the divider, the fold and the paging
                // cannot end up disagreeing about which side a chain belongs on — which is what a
                // second implementation here would eventually do. The local ledger below is only a
                // fallback for a payload that predates the field.
                const bandOf = (rs: TakenReturnEntry[]) => {
                  const v = Number((rs[0] as any).g_band);
                  if (v === 0 || v === 1) return v;
                  return groupStatus(rs).closed && workDoneOf(rs) ? 0 : 1;
                };
                // Open chains sort by the day they STARTED, settled ones by the day they FINISHED.
                const sortOf = (rs: TakenReturnEntry[]) => {
                  const v = Number((rs[0] as any).g_sort) || 0;
                  if (v) return v;
                  const all = rs.map(r => Number(r.ts) || 0);
                  return bandOf(rs) === 1 ? Math.min(...all) : Math.max(...all);
                };
                const keyOf = (rs: TakenReturnEntry[]) => String(rs[0].group_id || rs[0].id);
                groups.sort((A, B) => (bandOf(B) - bandOf(A)) || (sortOf(B) - sortOf(A)) || (keyOf(A) < keyOf(B) ? -1 : 1));
                // Where the archive starts. 0 means this page is all archive (page 2 and beyond) —
                // no divider then, there is nothing above it to divide from.
                const firstDone = groups.findIndex(g => bandOf(g) === 0);
                return groups.map((group, gi) => {
                  const linked = group.length > 1;
                  // Module frame + strip (owner 2026-08-25): a border around the whole group; the
                  // STRIP row carries the top edge and rounded top corners, data rows carry the sides
                  // and the last row the rounded bottom. Brown (Calem, Bugify #e2eb6886, 3rd opening:
                  // "make the green border Brown (same as the top menu for tracking/filing)"): the
                  // Document box's solid DOC_GROUP.color #BA7517, the fill of its selected Tracking /
                  // Filing tab. One line for every card, job or not. Was teal #4FB3BC. 1px, the width of
                  // the grey lines between its rows (Calem, Bugify #126f21bf: "Reduce all borders … for
                  // all bubbles from 2 px to 1 px"); was 2px. Filing's bubbles copy it (edge there).
                  // One shade darker since Calem, report ddf1ba0e, 3rd opening ("darken up the bubble
                  // border on each page by one shade"): #BA7517 is solid, with no see-through part to
                  // take on as the other pages' lines do, so it goes a quarter of the way to black,
                  // #8C5811 (see darken). The Document box and its selected tab keep #BA7517.
                  const FR = `1px solid ${darken(DOC_GROUP.color, 1)}`;
                  const R = 10;
                  const gs = groupStatus(group);
                  // Four states, not three (owner 2026-09-16, from Calem): "every document is back"
                  // and "this matter is finished" are different facts, and painting the first one
                  // green got it read as the second. Tania Carroll's passport came home on 14/09
                  // while two 11-month extensions were still running — green, "All returned", and
                  // the reasonable conclusion that nobody needed to look at it again.
                  const archived = bandOf(group) === 0;            // settled AND the work is over
                  const docsBack = !archived && gs.closed;         // settled, work still running
                  const chainJobs = chainJobsOf(group);
                  // Only CONFIRMED links speak for the chain; a proposal has its own prompt below.
                  const okJobs = chainJobs.filter((j: any) => !j.pending);
                  // A card with a job linked fills its strip with the brown of the Tracking top-menu
                  // button instead (Calem, Bugify #e2eb6886, 2nd opening: "If there is a job attached
                  // change the background to brown as per the menu color"), three shades lighter — the
                  // button's solid DOC_GROUP.color #BA7517 made #EEDDC5, see lighten. 3rd opening: "Make
                  // the brown header … two shades lighter": two shades on from the #DDBA8B it was is
                  // half-way from it to white, #BA7517's third shade; a fourth would be white. #DDBA8B
                  // was its second (Calem, Bugify #2bea0f21: "Change the tracking detail color [with
                  // job] to the button color … make it two shades lighter brown"), and the box's pale
                  // wash, DOC_GROUP.tint #FAEEDA, before that. One with none keeps its state's fill —
                  // "keep the background color the same", the Signboard Tax Letter card in Calem's
                  // example — and a job the system only proposed doesn't count until someone confirms
                  // it. A finished card's state — the folded archive — fills with the pale yellow of an
                  // open "Holding" strip, #FFF7E6 (Calem, Bugify #73dd2304: "On the collapsed/finished
                  // document tracking, use the brown color and the yellow color as a constant theme"),
                  // so the archive reads brown with a job and yellow without. That report's screenshot
                  // (V1.0.487) predates the 3rd opening and shows the #DDBA8B brown; the lighter #EEDDC5
                  // stays. 2nd opening: "Change the header fill color to be the same as the ticked
                  // version not the version with the cross" — ticked, an open Holding card with no job;
                  // crossed, a finished one on the box's pale wash, DOC_GROUP.tint #FAEEDA, which
                  // #FFF7E6 replaces. Green #E8F6EE before that. The state words keep their four colours
                  // on either fill — "All returned" green reads about 6.7:1 on the yellow, 5.4:1 on the
                  // brown; the day count stays darker on the brown (below).
                  const stripBg = okJobs.length ? lighten(DOC_GROUP.color, 3) : archived ? '#FFF7E6' : docsBack ? '#EFF6FF' : gs.atThird ? '#F3F0FF' : '#FFF7E6';
                  const stripCol = archived ? '#166534' : docsBack ? '#1D4ED8' : gs.atThird ? '#6D28D9' : '#92400E';
                  // ── Finished chains fold away (owner 2026-09-16) ────────────────────────────
                  // The list is a register of documents we are RESPONSIBLE for, so a chain folds
                  // only once nothing is outstanding — `gs.closed` means the last leg handed
                  // everything back to the owner. On top of that the work itself must be over:
                  // every linked job at a terminal level, or no job linked at all (owner's call:
                  // an unlinked chain folds on "all returned" alone).
                  // A chain where the job is done but a passport is still with us therefore stays
                  // open on purpose — that is precisely the row nobody may lose sight of.
                  const chainKey = group[0].group_id || group[0].id;
                  // A link the system PROPOSED and nobody has confirmed anywhere on this chain.
                  const pendingJob = chainJobs.find((j: any) => j.pending);
                  // Folding follows the BAND, so what is folded and what sits below the divider are
                  // the same set by construction — two rules would drift.
                  const foldable = archived;
                  const expanded = !foldable || openChains.has(chainKey);
                  // What the chain was FOR. A linked Job answers it outright; when none is linked the
                  // answer is usually sitting in the document notes anyway — every leg of a 90-day
                  // report chain literally says "90 Day Report" — so the most-repeated note stands in
                  // (owner 2026-09-16). Rendered in plain text rather than the Jobs pink, because an
                  // inferred label must not read as a confirmed Job link. Confirmed links only (okJobs,
                  // above); a proposal has its own prompt below.
                  // Just the first job's subject — no "+2" tail (owner 2026-09-16). The strip is a
                  // glance, and the hover lists every linked job in full.
                  const jobLabel = okJobs.length ? (purposeOf(okJobs[0].specific_job) || `#${okJobs[0].job_no}`) : '';
                  const noteLabel = jobLabel ? '' : noteSubject(group);
                  return (
                    <React.Fragment key={group[0].group_id || group[0].id}>
                      {/* Where the outstanding work ends and the archive begins (owner 2026-09-16),
                          the way read mail sits under unread. It carries its own top padding, so the
                          plain spacer stands down on this one row. */}
                      {gi === firstDone && firstDone > 0 && (
                        <tr>
                          <td colSpan={10} style={{ padding: '20px 12px 7px', border: 'none', background: 'var(--bg-surface)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 800, letterSpacing: .6, textTransform: 'uppercase', color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
                                <i className="ti ti-archive" style={{ fontSize: 12 }} aria-hidden />
                                {t('tr_band_done').replace('{n}', String(bands.done))}
                              </span>
                              <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                            </div>
                          </td>
                        </tr>
                      )}
                      {/* 9px → 13px (owner 2026-09-23, from Calem: about +3pt between chains) → 11px (Calem,
                          report e23c1d43: "Remove 2 pixels from vertical spacing between tracking events/bubbles")
                          → 8px (Calem, report fc6f9034: "Change the line spacing to match the one that is ticked
                          between each bubble"). The ticked gap is the 8px spacer under the column header at the
                          top of <tbody>, so the two now match; change them together. */}
                      {gi > 0 && gi !== firstDone && <tr aria-hidden="true"><td colSpan={10} style={{ padding: 0, height: 8, border: 'none', background: 'var(--bg-surface)' }} /></tr>}
                      {/* Status strip — one glance tells the chain's state (owner 2026-08-25 #2).
                          Folded, it is the whole record: who, what work, and the span it covers. */}
                      {/* The client's name leads the row, inside the box, ahead of every other fact
                          (owner 2026-09-23, from Calem): 13.5px bold, a size up from the rest of the
                          row. It spent one pass set into the frame's top edge like a fieldset legend;
                          Calem asked for it back in here at that same size, so the frame's line runs
                          unbroken again and the strip needs no extra top padding to clear it. Titles and
                          a company's legal form come off here and in the Client cell (Calem, report
                          101b5c69); the record and its emails keep the name as typed.
                          Now only on a finished card (Calem, report 02364eb7: "Remove the client name
                          from the tracking header … Keep the client's name in the header only for
                          collapsed / completed tracking"). `archived` is the one card that folds, and
                          folded, this strip is all that is left to name its client. An open card starts
                          on its state words instead — its rows below already name the client, in bold
                          in the Client cell. */}
                      <tr>
                        <td colSpan={10} style={{ padding: '5px 12px', background: stripBg, borderTop: FR, borderLeft: FR, borderRight: FR, borderTopLeftRadius: R, borderTopRightRadius: R, borderBottom: expanded ? '1px solid var(--border)' : FR, ...(expanded ? {} : { borderBottomLeftRadius: R, borderBottomRightRadius: R }) }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11.5, fontWeight: 800 }}>
                            {archived && (
                              <span style={{ color: 'var(--text-primary)', fontSize: 13.5, fontWeight: 800, whiteSpace: 'nowrap' }}>
                                {displayClientName(`${group[0].client_first || ''} ${group[0].client_last || ''}`) || '—'}
                              </span>
                            )}
                            <span style={{ color: stripCol }}>
                              {/* Both halves when both are true: a chain can hold one document and
                                  have another sitting at Immigration, and saying only one of those
                                  is how "All returned" got believed in the first place. */}
                              {archived ? t('tr_gs_closed') : docsBack ? t('tr_gs_docs_back') : [
                                gs.held ? t('tr_gs_holding').replace('{n}', String(gs.held)).replace('{o}', String(gs.heldOrig)) : '',
                                gs.atThirdN ? (gs.thirdName
                                  ? t('tr_gs_at_third').replace('{n}', String(gs.atThirdN)).replace('{name}', gs.thirdName)
                                  : `${t('tr_gs_third')} · ${gs.atThirdN}`) : '',
                              ].filter(Boolean).join(' · ')}
                            </span>
                            {/* What the chain was FOR, and the span it covers — the two facts that
                                identify a folded chain without opening it. */}
                            {/* The subject of the chain is what people scan for, so it carries a chip
                                rather than sitting in the run of text. A CONFIRMED job link wears the
                                top menu's Jobs box, with a briefcase (Calem, Bugify #88e53c4f: "Change
                                the color … (from purple) to the same color scheme (pink) as the top
                                menu for active and closed jobs"): its solid pink TAB_GROUPS.jobs.color
                                #EC4899 for the words, its line for the outline, and its see-through
                                wash laid on white first, #FEF2F8 like a plain Jobs row — laid on the
                                brown strip under the chip it would come out brown. The pink reads about
                                3.2:1 there, as the Active/Closed Jobs labels do on their box. Taken
                                from the palette, so it follows the box. Was indigo #4338CA on #EEF2FF,
                                outlined #C7D2FE. A subject merely inferred from the document notes
                                gets the same size and place but a dashed outline — equally easy to
                                read, still honest about where it came from. */}
                            {!!jobLabel && (
                              <span title={okJobs.map(j => `#${j.job_no} ${j.specific_job || ''}`).join(' · ')}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 800, color: TAB_GROUPS.jobs.color, background: lighten(TAB_GROUPS.jobs.tint, 0), border: `1px solid ${TAB_GROUPS.jobs.border}`, borderRadius: 20, padding: '2px 10px', maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <i className="ti ti-briefcase" style={{ fontSize: 12 }} aria-hidden />{jobLabel}
                              </span>
                            )}
                            {!!noteLabel && (
                              <span title={t('tr_chain_from_notes')}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 800, color: 'var(--text-primary)', background: 'var(--bg-surface)', border: '1px dashed var(--border-strong)', borderRadius: 20, padding: '2px 10px', maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <i className="ti ti-notes" style={{ fontSize: 12, opacity: .6 }} aria-hidden />{noteLabel}
                              </span>
                            )}
                            <span style={{ color: 'var(--text-secondary)', fontWeight: 700, whiteSpace: 'nowrap' }}>
                              {fmtDay(Number(group[0].ts))}{group.length > 1 ? ` – ${fmtDay(Number(group[group.length - 1].ts))}` : ''}
                            </span>
                            {gs.pending && (
                              <span style={{ fontSize: 10, fontWeight: 800, color: '#92400E', background: '#FEF3C7', border: '1px solid #FBD38D', borderRadius: 5, padding: '0 6px', whiteSpace: 'nowrap' }}>
                                <i className="ti ti-lock" style={{ fontSize: 10, marginRight: 3 }} aria-hidden />{t('tr_awaiting_approval')}
                              </span>
                            )}
                            <span style={{ flex: 1 }} />
                            {/* Pale grey all but vanished on a job's brown strip (Bugify #2bea0f21), so
                                there it takes the date's darker grey. It keeps it on the paler #EEDDC5
                                (#e2eb6886, 3rd opening): pale grey reads about 2.1:1 there, fainter
                                than on any other strip's fill (2.5–2.6:1); the darker grey about 5.2:1. */}
                            {!gs.closed && <span style={{ color: okJobs.length ? 'var(--text-secondary)' : 'var(--text-tertiary)', fontWeight: 700, whiteSpace: 'nowrap' }}>{t('tr_gs_days').replace('{d}', String(gs.days))}</span>}
                            {/* The system's proposal, awaiting a person. Deliberately in the strip
                                and not a modal: it is a passing glance decision, and it must be as
                                easy to reject as to accept. */}
                            {!!pendingJob && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 700, color: '#92400E', background: '#FEF3C7', border: '1px solid #FBD38D', borderRadius: 20, padding: '2px 4px 2px 10px', whiteSpace: 'nowrap', minWidth: 0, flexShrink: 1 }}>
                                <i className="ti ti-wand" style={{ fontSize: 12, flexShrink: 0 }} aria-hidden />
                                {/* The name may be long; the two buttons must never be pushed off. */}
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                                  {t('tr_link_suggest').replace('{job}', purposeOf(pendingJob.specific_job) || `#${pendingJob.job_no}`)}
                                </span>
                                <button onClick={ev => { ev.stopPropagation(); void api.trConfirmJobLink(group[0].id, pendingJob.id).then(load); }}
                                  title={t('tr_link_confirm')} style={{ flexShrink: 0, border: 'none', background: '#16A34A', color: '#fff', borderRadius: 20, padding: '2px 9px', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}>
                                  {t('tr_link_confirm')}
                                </button>
                                <button onClick={ev => { ev.stopPropagation(); void api.trRejectJobLink(group[0].id, pendingJob.id).then(load); }}
                                  title={t('tr_link_reject')} style={{ flexShrink: 0, border: '1px solid #FBD38D', background: 'transparent', color: '#92400E', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}>
                                  {t('tr_link_reject')}
                                </button>
                              </span>
                            )}
                            {/* Grey (Calem, Bugify #80c7b383: "gray color scheme for "expand chain""): the
                                Jobs countdown's grey bubble — a --c-gray-100 fill ringed in --c-gray-300 —
                                with the date's darker grey for the words and chevron, about 6:1 on the fill.
                                It shows only on a finished card, whose strip is the pale yellow or, with a
                                job, the brown; the fill stands apart from both, as it wouldn't on the bare
                                page. Was the app's green on white, like Email chain beside it. */}
                            {foldable && (
                              <button onClick={ev => { ev.stopPropagation(); setOpenChains(prev => { const n = new Set(prev); n.has(chainKey) ? n.delete(chainKey) : n.add(chainKey); return n; }); }}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, border: '1px solid var(--c-gray-300)', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', borderRadius: 7, padding: '2px 9px', fontSize: 11, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                <i className={`ti ti-chevron-${expanded ? 'up' : 'down'}`} style={{ fontSize: 12 }} aria-hidden />
                                {expanded ? t('tr_chain_collapse') : t('tr_chain_expand').replace('{n}', String(group.length))}
                              </button>
                            )}
                            {/* Owner 2026-08-29: in a dispute, send the client the WHOLE chain in
                                one email — every leg, laid out as it is here. Purple (Calem, Bugify
                                #80c7b383: "use purple (same as email/contacts/calendar menu) colors for
                                "email chain""): the top menu's Email / Contacts / Calendar box — its purple
                                EMAIL_CAL_GROUP.color #7F77DD for the words and icon, its line for the
                                outline, and its see-through wash laid on white first, #F3F2FC, like the job
                                chip's pink above — laid on a job's brown strip it would come out brown. The
                                purple reads about 3.4:1 on it, about what the green did on white. Taken from
                                the palette, so it follows the box. Was the app's green on white. */}
                            <button onClick={ev => { ev.stopPropagation(); setChainEmailFor(group[0].id); }} title={t('tr_ce_title')}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, border: `1px solid ${EMAIL_CAL_GROUP.border}`, background: lighten(EMAIL_CAL_GROUP.tint, 0), color: EMAIL_CAL_GROUP.color, borderRadius: 7, padding: '2px 9px', fontSize: 11, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              <i className="ti ti-mail-forward" style={{ fontSize: 12 }} aria-hidden />{t('tr_ce_btn')}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expanded && group.map((e, idx) => {
                        const taken = e.type === 'taken';
                        // Owner 2026-08-29: the leg that CLOSES the chain — the return to the owner —
                        // is a bookend, not a follow-up. It gets the head's flush margin and link icon
                        // so a chain reads as "opened here … closed here". Its background, like every
                        // row's, is the cream or stripe its place in the card gives it (above).
                        const closesChain = idx === group.length - 1 && e.type === 'returned' && e.counterparty !== 'third_party';
                        const isChild = linked && idx > 0 && !closesChain;   // a linked follow-up (not either end)
                        const baseBg = idx % 2 ? zebraBg : creamBg;
                        const gBot = idx === group.length - 1;
                        const fr = (ci: number): React.CSSProperties => ({
                          ...(gBot ? { borderBottom: FR } : {}),
                          ...(ci === 0 ? { borderLeft: FR, ...(gBot ? { borderBottomLeftRadius: R } : {}) } : {}),
                          ...(ci === 9 ? { borderRight: FR, ...(gBot ? { borderBottomRightRadius: R } : {}) } : {}),
                        });
                        return (
                          <tr key={e.id} onClick={() => { if (dragId) return; setEditing(e); setEditHead(idx === 0); setLinkSource(null); setShowForm(true); }}
                            draggable
                            onDragStart={ev => { ev.dataTransfer.setData('text/plain', e.id); ev.dataTransfer.effectAllowed = 'move'; setDragId(e.id); }}
                            onDragEnd={() => { setDragId(null); setDropOn(null); }}
                            onDragOver={ev => { if (dragId && dragId !== e.id) { ev.preventDefault(); ev.dataTransfer.dropEffect = 'move'; setDropOn(e.id); } }}
                            onDragLeave={() => setDropOn(d => (d === e.id ? null : d))}
                            onDrop={ev => { ev.preventDefault(); ev.stopPropagation(); const src = ev.dataTransfer.getData('text/plain') || dragId || ''; if (src && src !== e.id) rechain(src, { joinId: e.id }); else { setDragId(null); setDropOn(null); } }}
                            style={{ cursor: dragId ? 'grabbing' : 'pointer', background: dropOn === e.id ? '#DCFCE7' : baseBg, opacity: dragId === e.id ? 0.45 : 1, outline: dropOn === e.id ? '2px solid #1D9E75' : undefined }}
                            onMouseEnter={ev => { if (!dragId) ev.currentTarget.style.background = 'var(--bg-active)'; }}
                            onMouseLeave={ev => { if (!dragId) ev.currentTarget.style.background = baseBg; }}>
                            <td style={{ ...td, color: 'var(--text-secondary)', paddingLeft: linked ? (isChild ? 26 : 9) : 12, ...fr(0) }}>
                              {isChild
                                ? <><i className="ti ti-corner-down-right" style={{ marginRight: 5, color: 'var(--accent)' }} aria-hidden /><span style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)', background: '#fff', border: '1px solid var(--accent)', borderRadius: 5, padding: '0 5px', marginRight: 6 }}>{t('tr_linked')}</span></>
                                : linked ? <i className="ti ti-link" style={{ marginRight: 5, color: 'var(--accent)' }} aria-hidden /> : null}
                              {fmtTs(e.ts)}
                            </td>
                            <td style={{ ...td, ...fr(1) }}>
                              {/* Bold (Calem, report 02364eb7: "make client name bold in the tracking
                                  detail") — an open card's strip no longer names the client, so this is
                                  where the eye finds it. DM Sans is loaded up to 600, so 700 draws at
                                  600, as the strip's 800 does. The badges after it keep their own weights. */}
                              <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                                {displayClientName(`${e.client_first} ${e.client_last}`) || '—'}
                              </span>
                              {clientNotNotified(e) && (
                                <span title={t('tr_not_notified')} style={{ marginLeft: 6, display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 700, color: '#B45309', background: '#FEF3C7', borderRadius: 5, padding: '1px 5px', whiteSpace: 'nowrap' }}>
                                  <i className="ti ti-mail-off" style={{ fontSize: 11 }} />{t('tr_not_notified_short')}
                                </span>
                              )}
                              {!!e.mail_total && (
                                <MailBadge count={e.mail_total} inbound={e.mail_inbound} unread={e.mail_unread}
                                  last={e.mail_last_in_at} onClick={() => setMailFor(e)}
                                  title={e.mail_unread ? t('tr_mail_new_hint') : t('tr_mail_hint')} />
                              )}
                              {e.approval_status === 'pending' && (
                                <div style={{ marginTop: 3, display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', rowGap: 3 }}>
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 800, color: '#92400E', background: '#FEF3C7', border: '1px solid #FBD38D', borderRadius: 5, padding: '1px 6px', whiteSpace: 'nowrap' }}>
                                    <i className="ti ti-lock" style={{ fontSize: 11 }} />{t('tr_awaiting_approval')}
                                  </span>
                                  {isApprover && (e as any).approval_token && (
                                  <button onClick={ev => { ev.stopPropagation(); window.open(`https://d.slf-legal.co/api/ta/${(e as any).approval_token}`, '_blank'); }}
                                    title={t('qo_appr_open')}
                                    style={{ border: 'none', background: '#B45309', color: '#fff', borderRadius: 5, fontSize: 9.5, fontWeight: 800, padding: '1px 7px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                    <i className="ti ti-external-link" style={{ fontSize: 10, marginRight: 3 }} />{t('qo_appr_open')}
                                  </button>
                                  )}
                                  {/* Proof the request actually left the building. The mail CHAIN can
                                      only show it once IMAP has synced the sent copy back, which can
                                      take minutes — this reads the send stamp itself, so the operator
                                      never has to wonder whether the approver was told. */}
                                  <ApprovalMailChip notifiedAt={Number((e as any).approval_notified_at) || 0} notifiedTo={(e as any).approval_notified_to} />
                                </div>
                              )}
                              {/* Stale badge sits on its own SECOND line — inline it stretched the
                                  name column and broke the layout (owner 2026-08-25). */}
                              {e.notice_stale && (
                                <div style={{ marginTop: 3 }}>
                                <span title={t('tr_stale_hint')} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 800, color: '#fff', background: '#D0021B', borderRadius: 5, padding: '1px 6px', whiteSpace: 'nowrap' }}>
                                  <i className="ti ti-alert-triangle-filled" style={{ fontSize: 11 }} />{t('tr_stale_short')}
                                  <button onClick={ev => { ev.stopPropagation(); resendNotice(e); }} disabled={resending === e.id}
                                    style={{ marginLeft: 3, border: 'none', background: 'rgba(255,255,255,.25)', color: '#fff', borderRadius: 4, fontSize: 9.5, fontWeight: 800, padding: '1px 5px', cursor: resending === e.id ? 'wait' : 'pointer' }}>
                                    {resending === e.id ? '…' : t('tr_resend')}
                                  </button>
                                </span>
                                </div>
                              )}
                            </td>
                            <td style={{ ...td, ...fr(2) }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 600, color: taken ? '#E24B4A' : '#1D9E75' }}>
                                <i className={`ti ti-${taken ? 'circle-arrow-down' : 'circle-arrow-up'}`} style={{ fontSize: 14 }} aria-hidden />
                                {taken ? t('tr_taken') : t('tr_returned')}
                              </span>
                              {/* Counterparty badge on EVERY leg (owner 2026-08-25) — client legs used
                                  to carry no label, so from/to whom was only visible in the detail. */}
                              <div style={{ marginTop: 3 }}>
                                {e.counterparty === 'third_party' ? (() => {
                                  // Calem 2026-09-02: colour says direction AND kind at a glance —
                                  //   to dept = pink · from dept = purple · to individual = light blue · from individual = royal blue.
                                  // Legacy rows have no kind; a name on the department list reads as a department.
                                  const dept = (e as any).third_party_kind === 'department'
                                    || (!(e as any).third_party_kind && TR_DEPARTMENTS.some(d => d.toLowerCase() === String(e.third_party_name || '').trim().toLowerCase()));
                                  const c = dept
                                    ? (taken ? { fg: '#7C3AED', bg: '#F3F0FF', bd: '#DDD6FE' } : { fg: '#DB2777', bg: '#FDF2F8', bd: '#FBCFE8' })
                                    : (taken ? { fg: '#2563EB', bg: '#EFF6FF', bd: '#BFDBFE' } : { fg: '#0284C7', bg: '#F0F9FF', bd: '#BAE6FD' });
                                  // In fitCell, so a long name can't hold Taken or Returned wide (Bugify #870a98b0, 4th
                                  // opening): the tag stops at the cell's edge and the name ends in "…". Hovering names it
                                  // whole, ahead of the email the hover showed alone before.
                                  return (
                                  <div style={fitCell}>
                                  <span title={[e.third_party_name, e.third_party_email].filter(Boolean).join(' · ')} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, maxWidth: '100%', fontSize: 10.5, fontWeight: 700, color: c.fg, background: c.bg, border: `1px solid ${c.bd}`, borderRadius: 6, padding: '1px 7px', whiteSpace: 'nowrap' }}>
                                    <i className={`ti ti-${dept ? 'building-bank' : 'user'}`} style={{ fontSize: 10 }} aria-hidden />
                                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{taken ? t('tr_cp_from_short') : t('tr_cp_to_short')} {e.third_party_name || t('tr_cp_third')}</span>
                                  </span>
                                  </div>
                                  );
                                })() : (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10.5, fontWeight: 700, color: '#0F766E', background: '#ECFDF5', border: '1px solid #99F6E4', borderRadius: 6, padding: '1px 7px', whiteSpace: 'nowrap' }}>
                                    <i className="ti ti-user" style={{ fontSize: 10 }} aria-hidden />
                                    {taken ? t('tr_cp_from_short') : t('tr_cp_to_short')} {t('tr_cp_client')}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td style={{ ...td, whiteSpace: 'normal', minWidth: 240, color: 'var(--text-secondary)', ...fr(3) }}>
                              {/* Per-item records list one line per document WITH its note (owner
                                  2026-08-25: an amount typed on a Cash item must show in the quick
                                  view, not only inside the record). Legacy records keep the joined
                                  type list. */}
                              {(e as any).doc_items?.length
                                ? ((e as any).doc_items as any[]).map((i, k) => (
                                    <div key={k} style={{ lineHeight: 1.4 }}>
                                      {/* A colon straight after the name, not a dash before the note (Calem, report
                                          fcc6685f, 8th opening): "Fitness License: 112-12 · Fit Koh Samui (Complete)".
                                          A Copy/scan or kept badge sits between it and the note, as in the hand-back list. */}
                                      {docLabel(i.type, e.type)}{i.note && ':'}
                                      {/* Calem 2026-09-03: a lone "e" was too cryptic — say Copy/scan outright. */}
                                      {!i.original && <span title="Electronic / scan copy" style={{ fontSize: 10, fontWeight: 800, color: '#6D28D9', background: '#F3F0FF', border: '1px solid #DDD6FE', borderRadius: 4, padding: '0 5px', marginLeft: 5, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>Copy/scan</span>}
                                      {/* Says WHY a chain could settle with this item still listed —
                                          without it, "All returned" next to a cash line looks wrong. */}
                                      {i.kept && <span title={t('tr_kept_tip')} style={{ fontSize: 10, fontWeight: 800, color: '#B45309', background: '#FEF6E7', border: '1px solid #F0DCA6', borderRadius: 4, padding: '0 5px', marginLeft: 5, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>{t('tr_kept_badge')}</span>}
                                      {i.note && <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}> {shownNote(i.type, i.note)}</span>}
                                    </div>
                                  ))
                                : (Object.keys(((e as any).doc_notes || {})).length
                                  ? e.documents.map((d, k) => (
                                      <div key={k} style={{ lineHeight: 1.4 }}>
                                        {docLabel(d, e.type)}
                                        {((e as any).doc_notes || {})[d] && <>:<span style={{ color: 'var(--text-primary)', fontWeight: 600 }}> {shownNote(d, ((e as any).doc_notes || {})[d])}</span></>}
                                      </div>
                                    ))
                                  : e.documents.map(d => docLabel(d, e.type)).join(', '))}
                            </td>
                            {/* Owner 2026-08-25 layout pass: total + originals share ONE cell per
                                direction ("3 (1)"), freeing width for the Documents column. */}
                            <td style={{ ...numTd, whiteSpace: 'nowrap', paddingRight: 6, ...fr(4) }}>
                              {taken ? <><span style={{ fontWeight: 600 }}>{e.count_total}</span> <span style={{ color: 'var(--text-tertiary)' }}>({e.count_originals})</span></> : ''}
                            </td>
                            <td style={{ ...numTd, whiteSpace: 'nowrap', paddingLeft: 6, ...fr(5) }}>
                              {!taken ? <><span style={{ fontWeight: 600 }}>{e.count_total}</span> <span style={{ color: 'var(--text-tertiary)' }}>({e.count_originals})</span></> : ''}
                            </td>
                            <td style={{ ...td, whiteSpace: 'normal', color: 'var(--text-secondary)', ...fr(6) }}>{e.comments}</td>
                            {/* Who touched this record (owner 2026-08-27): creator on top; the
                                last editor below it, only shown once the record has been edited. */}
                            {/* Job links (owner 2026-09-02): explicit M:N — chips open the job,
                                ⊕ opens the picker (suggests the person's ACTIVE jobs; operator decides). */}
                            <td style={{ ...td, whiteSpace: 'normal', minWidth: 90, ...fr(7) }} onClick={ev => ev.stopPropagation()}>
                              {/* Each chip is dressed as the Jobs toolbar's unselected pills (Calem, report 85ddfdbc, 5th
                                  opening: "Change these colored bubbles to the same pink as the job menu bubbles"): menuPill's
                                  pink #EC4899 words on the Jobs box's see-through 0.07 wash, and the pills' line, the box's a
                                  shade darker (0.49 → 0.62, JOBS_LINE in Jobs). Taken from the palette, so they follow the box.
                                  Shape, size and click-through were kept, and the dashed + was unchanged. Were indigo #4F46E5
                                  on a 0.08 wash, outlined at 0.3. Then one width for every chip (its 6th opening: "Make all of
                                  these job bubbles the same width"): each was as wide as its own number, a 1 narrower than a 0,
                                  so #61,310, #61,311, #60,855 and #61,296 came out a few px apart. Now each is at least 60px
                                  with its number centred, in DM Sans's equal-width figures (tabular-nums). A five-figure
                                  '#61,310' at 10.5px (drawn at 600, the heaviest weight loaded) needs about 59px at the most,
                                  padding and border included (estimated from DM Sans's letter widths, not measured on screen);
                                  a shorter number sits centred in the 60px and a longer one still widens its chip. Height,
                                  corners, colours, click-through and the dashed + are unchanged. */}
                              {(e as any).jobs?.map((j: any) => (
                                <span key={j.id} title={j.specific_job || ''} onClick={() => window.open(`/m/active_jobs?focus=${j.id}&scope=all`, '_blank')}
                                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 3, minWidth: 60, margin: '1px 4px 1px 0', border: '1px solid', borderRadius: 999, padding: '0 7px', fontSize: 10.5, fontWeight: 800, fontVariantNumeric: 'tabular-nums', cursor: 'pointer', whiteSpace: 'nowrap', ...menuPill('active_jobs', false), borderColor: darken(TAB_GROUPS.jobs.border, 1) }}>
                                  #{Number(j.job_no).toLocaleString()}
                                </span>
                              ))}
                              <button title={t('tr_jl_title')} onClick={() => setJobLinkFor(e)}
                                style={{ border: '1px dashed var(--border-strong)', background: 'none', color: 'var(--text-tertiary)', borderRadius: 999, width: 20, height: 20, cursor: 'pointer', fontSize: 12, lineHeight: 1, padding: 0, verticalAlign: 'middle' }}>+</button>
                            </td>
                            <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 11.5, color: 'var(--text-secondary)', padding: '10px 8px', ...fr(8) }}>
                              <div title={(e as any).created_by_name || ''} style={byLine}>{(e as any).created_by_name || '—'}</div>
                              {!!(e as any).edited && (
                                <div title={(e as any).updated_by_name || ''} style={{ ...byLine, color: 'var(--text-tertiary)', marginTop: 1 }}>
                                  <i className="ti ti-pencil" style={{ fontSize: 10, marginRight: 3 }} aria-hidden />{(e as any).updated_by_name || '—'}
                                </div>
                              )}
                            </td>
                            <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right', ...fr(9) }}>
                              <button onClick={ev => { ev.stopPropagation(); setEditing(null); setLinkSource(e); setLinkChain(group); setShowForm(true); }} title={t('tr_link_add')}
                                style={{ width: 24, height: 24, borderRadius: 6, border: '1px solid var(--accent)', background: 'transparent', color: 'var(--accent)', cursor: 'pointer', fontSize: 15, lineHeight: 1, marginRight: 6 }}>+</button>
                              <i className="ti ti-chevron-right" style={{ color: 'var(--text-tertiary)' }} aria-hidden />
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                });
              })()}
              {filtered.length === 0 && (
                <tr><td style={{ ...td, textAlign: 'center', color: 'var(--text-tertiary)', padding: 40 }} colSpan={10}>{t('tr_empty')}</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <Pagination page={page} total={total} limit={pageSize} onPage={p => load(p)} onLimit={n => setPageSize(n)} />

      {/* The shared floating "+", in the top menu's Document (Tracking) colour (Bugify #7568b3b5) */}
      <Fab onClick={() => { setEditing(null); setLinkSource(null); setLinkChain(null); setShowForm(true); }} title={t('tr_add')} color={tabColor('taken_return')} />

      {/* Drag a record here to pull it OUT of its chain — only visible while dragging, and it sits
          on top of the floating + so it can't be missed. */}
      {dragId && (
        <div
          onDragOver={ev => { ev.preventDefault(); ev.dataTransfer.dropEffect = 'move'; setDropOn('__out__'); }}
          onDragLeave={() => setDropOn(d => (d === '__out__' ? null : d))}
          onDrop={ev => { ev.preventDefault(); const src = ev.dataTransfer.getData('text/plain') || dragId || ''; rechain(src, { groupId: '' }); }}
          style={{ position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 22, zIndex: 200, padding: '13px 26px', borderRadius: 12,
            border: `2px dashed ${dropOn === '__out__' ? '#B45309' : '#D97706'}`, background: dropOn === '__out__' ? '#FEF3C7' : '#FFFBEB',
            color: '#92400E', fontSize: 13.5, fontWeight: 800, boxShadow: 'var(--shadow-md)', pointerEvents: 'auto' }}>
          <i className="ti ti-unlink" style={{ marginRight: 7 }} aria-hidden />{t('tr_drop_unlink')}
        </div>
      )}
      {jobLinkFor && <JobLinkPicker entry={jobLinkFor} onClose={() => setJobLinkFor(null)} onSaved={() => load()} />}
      {chainEmailFor && <ChainEmail legId={chainEmailFor} onClose={() => setChainEmailFor(null)}
        onSent={(to: string) => { showToast(t('tr_ce_sent').replace('{to}', to)); load(); }} />}
      {mailFor && <MailPanel entry={mailFor} onClose={() => setMailFor(null)} onSeen={() => load()} />}
      {showForm && <EntryForm entry={editing} chainHead={editHead} linkSource={linkSource} chain={linkChain || undefined} firms={firms} defaultCompany={me?.companyId || null} onClose={() => setShowForm(false)} onSaved={() => load()} />}
    </div>
  );
}

// ── Standalone module page (/m/taken_return) ─────────────────────────────────
export default function TakenReturn() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <button onClick={() => navigate('/')} title="SLF" style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img className="app-logo" src="/logo.png" alt="SLF" style={{ width: 28, height: 28, objectFit: 'contain' }} />
        </button>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{t('mod_taken_return')}</div>
      </header>
      <TakenReturnView />
    </div>
  );
}
